import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Collapse, Tooltip, Typography } from '@mui/material';
import { mergeSx } from '@client/helpers/mui.helpers';
import { Menu } from '@ui/menus/Menu';
import { useApplyProjectChanges } from '@client/store/project/use-apply-project-changes.hook';
import {
  buildBulkDiscardCandidateRowChanges,
  getDiscardTriggerByRowId,
  getStashedElementsByRowId,
  isRowDiscarded,
} from '@shared/helpers/candidate-discard.helpers';
import { DiscardTrigger, IElement } from '@shared/models/project.model';
import { IProduct } from '@shared/models/product.model';
import { Recipe } from '@shared/models/recipe.model';
import { isDeletedRecipe } from '@shared/helpers/recipe.helpers';
import {
  ImportRowDraft,
  MappingReferenceType,
} from '@shared/models/element-import.model';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { getElementCategory } from '@shared/helpers/element-category.helpers';
import { ConversionFactorUnit } from '@shared/models/unit.model';
import {
  candidateConversion,
  candidateQuantityFrom,
  candidateUnitFrom,
  tryMaterializeCandidateRow,
} from '@shared/helpers/materialize-candidate.helpers';
import {
  formatIfcMeasureMenuLabel,
  getIfcMeasureMenuParts,
  IFC_MEASURE_MENU_GROUP_LABEL,
  listAvailableIfcMeasures,
  rawWithActiveIfcQuantity,
  resolveActiveIfcMeasure,
  rowWithActiveIfcMeasure,
  type IfcMeasure,
} from '@shared/helpers/ifc-quantity.helpers';
import {
  resolveImportQuantity,
  type ImportTarget,
} from '@shared/helpers/import-quantity-resolution.helpers';
import { mergeReimportedElement } from '@shared/helpers/csv-import-reimport.helpers';
import {
  importReaddParentId,
  placeRemappedImportAdds,
} from '@shared/helpers/import-replace.helpers';
import { selectedProposalIdForVersion } from './import-replace-target.helpers';
import {
  ProjectChange,
  ProjectChangeType,
} from '@shared/helpers/pipeline/update-engine.models';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import {
  getElementIconCategoryName,
  getProductCategoryIconName,
} from '@features/elements/helpers/get-element-category-icon-name.helper';
import { useImportedRowEditing } from './use-imported-row-editing.hook';
import { MappingPicker } from './MappingPicker';
import {
  buildSwitchMappingOverlay,
  deriveQuantityMappingEntry,
  deriveTypedTotalMappingEntry,
  getImportQuantityOverride,
  getProductDefaultQuantityName,
  getSelectableQuantityNames,
  getSelectableQuantityNamesForRecipe,
  inverseQtyToFromEntry,
  importRowDraftPatchForIfcMeasure,
  ifcMeasureHasAutoConversion,
  isMappingCalibrationCompatible,
} from './in-row-mapping.helpers';
import {
  ElementQuantityExpressionName,
  elementQuantityUnitRecord,
} from '@shared/models/element-quantities.model';
import { labelFromValue } from '@shared/helpers/string.helpers';
import { isQuantityIconName } from '@ui/Icon/Icon';
import { EllipsisText } from '@ui/EllipsisText';
import { makeStyles } from 'tss-react/mui';
import { useSelectedElementId, useSelectedVersion } from '@client/store/ui';
import ElementListRow, {
  ELEMENT_LIST_ROW_HEIGHT,
  ElementListRowHandle,
  SelectedBlockPosition,
  ValueField,
} from '@features/proposals/ElementListRow/ElementListRow';
import { ELEMENT_LIST_WIDTH_CSS } from '@features/proposals/ElementListRow/element-list-column-resize.hook';
import { ELEMENT_LIST_HEADER_HEIGHT } from '@features/proposals/ElementListRow/element-list.constants';
import {
  createParkedListTargetStabilizer,
  parkListSectionWhenReady,
  scrollCollapsedStickySectionIntoPlace,
  scrollElementBelowListHeader,
  untilNextLayout,
} from '@features/proposals/ElementListRow/element-list-scroll.helpers';
import {
  consumeScrollRequest,
  requestScrollToElement,
} from '@features/proposals/ElementListRow/element-scroll-request.store';
import {
  getSelectedBlockPosition,
  intersectSelectedWithVisible,
  resolveBulkActionTargetIds,
  selectedBlockPositionBelowFileHeader,
} from './list-multi-select.helpers';
import { useFirstRowFlushWithFileHeader } from './use-first-row-flush-with-file-header.hook';
import {
  useBulkRowActions,
  type BulkRowActions,
} from './use-bulk-row-actions.hook';
import {
  useAiImportMapping,
  type AiImportMappingActions,
} from './use-ai-import-mapping.hook';
import { aiImportMappingMenuLabel } from './ai-import-mapping-apply.helpers';
import { useImportMappingClipboardHotkeys } from './use-import-mapping-clipboard.hook';
import { HideOutsideViewport } from '@ui/HideOutsideViewport';
import { SharedIntersectionObserverProvider } from '@client/hooks/use-intersection-observer.hooks';
import { setMainHorizontalScrollTarget } from '@client/helpers/main-horizontal-scroll.store';
import { createScrollSyncRegistry } from './scroll-sync.helpers';
import {
  CandidateRow,
  ProjectElementImport,
} from '@shared/models/element-import.model';
import {
  buildHeaderLabels,
  headerKey,
  readKalkylfilRawCell,
  sortColumnsByNodonPriority,
} from '@shared/helpers/kalkylfil-csv.parser';
import { useShowUnusedColumns } from './show-unused-columns.hook';
import { useProductsLookup } from '@client/store/product';
import { useRecipeLookup } from '@client/store/recipe/recipe.hook';
import { useRecipes } from '@client/store/recipe/recipe.hook';
import { useExpandedItems } from '@client/hooks/use-expand-elements.hooks';
import {
  closeMenu,
  menuMouseHandler,
  openMenu,
  useIsMenuOpen,
  useMenuPosition,
} from '@client/hooks/use-menu.hooks';
import { useProjectNavigation } from '@client/hooks/use-router.hooks';
import { Page } from '@client/store/ui/ui.model';
import { getCandidateRowElementDisplay } from './candidate-row-display.helpers';
import { MappingsByCode } from './element-imports.store';
import { useElementImports } from './use-element-imports.hook';
import {
  formatImportLabel,
  formatImportProgressLabel,
} from './import-display.helpers';
import { ImportFileRowLabel } from './ImportFileRowLabel';
import { setCsvImportFileExpanded } from './import-list-expand.helpers';
import {
  getImportsNewestFirst,
  sortCandidateRows,
  sortCandidateRowsByColumn,
} from './import-list-navigation.helpers';
import {
  getVisibleListRowIds,
  setVisibleListRowIds,
  clearVisibleListRowIds,
  useListSelection,
} from './list-selection.store';
import {
  cycleImportSort,
  ImportSort,
  loadImportSort,
  resolveImportSort,
  saveImportSort,
} from './import-sort.ls';
import {
  loadImportStatusFilter,
  saveImportStatusFilter,
} from './import-status-filter.ls';
import {
  CANDIDATE_STATUS_LABEL,
  CandidateRowStatus,
  collectImportedElements,
  useImportedElements,
  computeImportProgressFromStatusCounts,
  getStatusSummaryGroupKey,
  isStatusGroupHidden,
  ProductsById,
  RecipesById,
  resolveCandidateProduct,
  resolveCandidateRecipe,
  resolveCandidateStatus,
  resolveMappedRowProductId,
  resolveMappedRowRecipeId,
  STATUS_SUMMARY_GROUPS,
  statusSummaryFilterTooltip,
  StatusSummaryGroupKey,
  toggleStatusSummaryGroupHidden,
} from './candidate-status.helpers';
import {
  STATUS_COLOR,
  STATUS_ICON,
  STATUS_ICON_OUTER_SIZE_PX,
} from './candidate-status-display';
import { useCandidateSearchFilter } from './use-candidate-search.hook';
import {
  clampImportColumnWidthPx,
  computeImportColumnWidths,
} from './import-column-widths.helpers';
import {
  formatImportTableCellDisplay,
  shouldFormatImportTableCellAsNumber,
} from './import-table-cell-display.helpers';
import { removeNumberFormatting } from '@shared/helpers/math.helpers';
import {
  loadImportColumnWidths,
  saveImportColumnWidths,
} from './import-column-widths.ls';
import {
  buildImportRowDraftReplaceChange,
  draftAfterRemovingMapping,
  draftToOriginMapping,
  getImportRowDraft,
  importRowDraftHasRemovableMapping,
} from '@shared/helpers/import-row-draft.helpers';
import { useImportRowDraft } from './use-import-row-draft.hook';
import { KineticNumberDisplay } from '@ui/KineticNumberDisplay';
import { Icon } from '@ui/Icon';
import {
  WasteScale,
  wasteCellToPercent,
} from '@shared/helpers/waste-scale.helpers';
import { useAutoImportCandidates } from './use-auto-import.hook';
import { ImportKebabMenu } from './ImportKebabMenu';
import { CandidateSaveMappingButton } from './CandidateSaveMappingButton';
import { CandidateAiMappingButton } from './CandidateAiMappingButton';
import { SaveMappingDialog } from './SaveMappingDialog';
import { useCandidateSaveMapping } from './use-candidate-save-mapping.hook';
import { saveMappingActionLabel } from './save-mapping-action.helpers';
import {
  STATUS_FILTER_ROW_COLLAPSE_MS,
  useAnimatedStatusFilterRows,
} from './use-animated-status-filter-rows.hook';

type StatusCounts = Partial<Record<CandidateRowStatus, number>>;

/**
 * Empty mapping table used for rows that have no materialized element yet. We
 * deliberately do NOT consult the DB resource-code mappings for such rows: the
 * importable rows are materialized once at file drop, and any leftover row is
 * handled manually per-row via its draft (so a mapping saved to one row never
 * leaks onto sibling rows that share the same resurskod). Only already-imported
 * rows resolve their mapping by code (for `stored`-source origins).
 */
const EMPTY_MAPPINGS: MappingsByCode = {};

const statusMetricTextSx = {
  color: 'rgba(0,0,0,0.5)',
  fontSize: 10,
  fontVariantNumeric: 'tabular-nums',
  fontWeight: 600,
  lineHeight: `${STATUS_ICON_OUTER_SIZE_PX}px`,
} as const;

const importProgressTextSx = {
  ...statusMetricTextSx,
  whiteSpace: 'nowrap',
} as const;

const sortableHeaderCursorSx = { cursor: 'pointer' } as const;

/** Padding so absolute status chips don't collide with the file name / kebab. */
const IMPORT_HEADER_STATUS_NAME_PAD = '150px';

// ---------------------------------------------------------------------------
// Shared column widths -- header + every row read the same array via Context
// so a drag on the header strip propagates instantly to all candidate rows
// (mirrors the DEV-3144 POC's `ImportStripeContext`).
// ---------------------------------------------------------------------------

type StripeCtx = {
  columnWidths: number[];
  onResizePointerDown:
    | ((columnIndex: number) => React.PointerEventHandler<HTMLDivElement>)
    | undefined;
  /** Tag.Header column key → original CSV header, for the header row. */
  headerLabels: Record<string, string>;
  /** Active row sort, so the header marks the sorted column + direction. */
  sort: ImportSort | undefined;
  /** Click a header to cycle its sort (asc → desc → cleared). */
  onSortHeader: ((column: string) => void) | undefined;
  /** CSV header name that maps to the quantity field, for display formatting. */
  quantityHeader?: string;
  /** CSV header name that maps to the unit field (IFC measure pick target). */
  unitHeader?: string;
  /** CSV header name that maps to the waste field, for display formatting. */
  wasteHeader?: string;
};

const ImportStripeContext = React.createContext<StripeCtx | null>(null);

// ---------------------------------------------------------------------------
// Horizontal scroll-sync -- keeps every metadata strip aligned while scrolling.
// ---------------------------------------------------------------------------

type ScrollSyncCtx = {
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  registerRef: React.RefCallback<HTMLDivElement>;
};

const ScrollSyncContext = React.createContext<ScrollSyncCtx | null>(null);

const useScrollSync = (): ScrollSyncCtx => {
  const sync = React.useRef(createScrollSyncRegistry<HTMLDivElement>());

  const registerRef = React.useCallback<React.RefCallback<HTMLDivElement>>(
    (el) => {
      setMainHorizontalScrollTarget(sync.current.register(el));
    },
    [],
  );
  const onScroll = React.useCallback((e: React.UIEvent<HTMLDivElement>) => {
    sync.current.applyScrollLeft(e.currentTarget, e.currentTarget.scrollLeft);
  }, []);

  useEffect(
    () => () => {
      sync.current.clear();
      setMainHorizontalScrollTarget(null);
    },
    [],
  );

  return { onScroll, registerRef };
};

const useStyles = makeStyles<void, 'resizeHandle' | 'measurePickWash'>()(
  (theme, _params, classes) => ({
    cell: {
      alignItems: 'center',
      alignSelf: 'stretch',
      display: 'flex',
      flexShrink: 0,
      overflow: 'hidden',
      position: 'relative',
      paddingRight: 8,
    },
    // Thin column guides, centered in each resize hit target while the header
    // strip is hovered.
    headerScrollStrip: {
      [`&:hover .${classes.resizeHandle}::after`]: {
        backgroundColor: 'rgba(0, 0, 0, 0.12)',
      },
    },
    // Compact hit target; 5px matches {@link PillTabs} default tabs.
    measurePickHit: {
      alignItems: 'center',
      background: 'none',
      border: 'none',
      borderRadius: '5px',
      boxSizing: 'border-box',
      cursor: 'pointer',
      display: 'flex',
      height: 24,
      maxWidth: '100%',
      minWidth: 0,
      paddingLeft: 4,
      paddingRight: 4,
      width: 'fit-content',
      '&:hover, &[data-open]': {
        backgroundColor: 'rgba(0,0,0,0.05)',
      },
    },
    // Full Quantity+Unit slot; 2px bridge matches strip gap and keeps wash solid.
    measurePickGroup: {
      alignItems: 'center',
      alignSelf: 'center',
      background: 'none',
      border: 'none',
      boxSizing: 'border-box',
      cursor: 'pointer',
      display: 'flex',
      flexShrink: 0,
      gap: 0,
      height: 24,
      minWidth: 0,
      padding: 0,
      [`&:hover .${classes.measurePickWash}, &[data-open] .${classes.measurePickWash}`]:
        {
          backgroundColor: 'rgba(0,0,0,0.05)',
        },
    },
    measurePickQtySlot: {
      alignItems: 'center',
      boxSizing: 'border-box',
      display: 'flex',
      flexShrink: 0,
      justifyContent: 'flex-end',
      minWidth: 0,
      overflow: 'hidden',
    },
    measurePickUnitSlot: {
      alignItems: 'center',
      boxSizing: 'border-box',
      display: 'flex',
      flexShrink: 0,
      justifyContent: 'flex-start',
      minWidth: 0,
      overflow: 'hidden',
    },
    // Pill wash: qty | bridge | unit — one continuous hover chip.
    measurePickWash: {
      alignItems: 'center',
      boxSizing: 'border-box',
      display: 'flex',
      height: 24,
      maxWidth: '100%',
      minWidth: 0,
      transition: 'background-color 180ms ease',
    },
    // Nudge qty left of the column seam so digits stay in Quantity only.
    measurePickWashQty: {
      borderRadius: '5px 0 0 5px',
      paddingLeft: 4,
      paddingRight: 4,
    },
    // Fills the strip column gap so hover has no visual break.
    measurePickWashBridge: {
      flexShrink: 0,
      width: 2,
    },
    measurePickWashUnit: {
      borderRadius: '0 5px 5px 0',
      paddingLeft: 4,
      paddingRight: 4,
      width: 'fit-content',
    },
    resizeHandle: {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      width: 6,
      cursor: 'col-resize',
      touchAction: 'none',
      zIndex: 1,
      // Hairline guide, centered in the 6px hit box (shown on header-strip hover).
      '&::after': {
        backgroundColor: 'transparent',
        bottom: 4,
        content: '""',
        left: '50%',
        pointerEvents: 'none',
        position: 'absolute',
        top: 4,
        transform: 'translateX(-50%) scaleX(0.5)',
        width: 1,
      },
      '&:hover, &:active': { backgroundColor: 'rgba(0,0,0,0.18)' },
    },
    statusSummaryColumn: {
      boxSizing: 'border-box',
      flexShrink: 0,
      flexWrap: 'nowrap',
      paddingLeft: 4,
      paddingRight: 4,
      whiteSpace: 'nowrap',
    },
    statusSummarySegment: {
      alignItems: 'center',
      background: 'none',
      border: 'none',
      color: 'inherit',
      cursor: 'pointer',
      display: 'inline-flex',
      flexShrink: 0,
      font: 'inherit',
      gap: '2px',
      padding: 2,
      whiteSpace: 'nowrap',
      borderRadius: 5,
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      },
    },
    statusSummarySegmentHidden: {
      opacity: 0.35,
    },
    scrollStrip: {
      alignSelf: 'stretch',
      overflowX: 'auto',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
    },
    candidateRow: {
      position: 'relative',
      '&:hover [data-candidate-row-hover-action], &:focus-within [data-candidate-row-hover-action]':
        {
          pointerEvents: 'auto',
          visibility: 'visible',
        },
      '& [data-candidate-row-hover-action][data-open]': {
        pointerEvents: 'auto',
        visibility: 'visible',
      },
      '&:not([data-discarded]):hover [data-go-to-element-action]': {
        pointerEvents: 'auto',
        visibility: 'visible',
      },
      '&:hover[data-discarded] [data-skipped-row-unskip-hint]': {
        visibility: 'visible',
      },
    },
    mappingPickerHidden: {
      minWidth: 0,
      pointerEvents: 'none',
      visibility: 'hidden',
    },
    // Between import files (kept outside sticky so it doesn't sit above the name).
    importGroupDivider: {
      display: 'flex',
      boxSizing: 'border-box',
      height: 5,
      width: '100%',
      backgroundColor: theme.palette.warmSurface.main,
      borderTop: '1px solid rgba(0, 0, 0, 0.1)',
    },
    // Same inset as ElementListRow / footer so the frozen-column rule does
    // not break across file gaps or leftover space under the last file.
    importColumnRail: {
      flexShrink: 0,
      width: ELEMENT_LIST_WIDTH_CSS,
      boxShadow: 'inset -1px 0 0 rgba(0, 0, 0, 0.1)',
    },
    importSectionRows: {
      display: 'flex',
      flexDirection: 'column',
    },
    importHeaderDivider: {
      width: '100%',
      borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
    },
    importHeaderDividerSelected: {
      borderBottomColor: '#0B99FF',
    },
    firstRowSentinel: {
      height: 0,
    },
    // Sit under the sticky ElementListHeader while scrolling this file's rows.
    stickyImportHeader: {
      backgroundColor: theme.palette.warmSurface.main,
      // Opaque stack so row status/KPI (also z-index 1) can't paint through.
      isolation: 'isolate',
      position: 'sticky',
      top: ELEMENT_LIST_HEADER_HEIGHT,
      width: '100%',
      // Below ElementListHeader (z-index 3). Same z-index as the tabs lets an
      // unsticking first file paint over Elements/Products/Imports.
      zIndex: 2,
    },
    goToElementAction: {
      pointerEvents: 'none',
      visibility: 'hidden',
    },
    fileHeaderKebab: {
      alignItems: 'center',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      flexShrink: 0,
      marginLeft: 2,
      padding: 4,
      borderRadius: 5,
      color: 'rgba(0,0,0,0.6)',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      },
    },
  }),
);

/**
 * Both discarded states (manual DISCARDED and saved-mapping AUTO-DISCARDED)
 * share the same row treatment: greyed/disabled row, "Restore row" affordance,
 * and edit-lock (DEV-3360).
 */
const isDiscardedStatus = (status: CandidateRowStatus | undefined): boolean =>
  status === 'discarded' || status === 'autoDiscarded';

/**
 * Same chrome as TypeaheadField's closed “Select product or recipe”: 4px
 * extra side pad around {@link ValueField}'s 2px, so the label lines up.
 * Hidden until the discarded row is hovered; only this box unskips.
 */
const useSkippedRowUnskipHintStyles = makeStyles()(() => ({
  root: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    minWidth: 0,
  },
  field: {
    alignItems: 'center',
    alignSelf: 'center',
    display: 'flex',
    flex: '0 1 auto',
    maxWidth: '100%',
    minWidth: 0,
    paddingLeft: 4,
    paddingRight: 4,
    position: 'relative',
    visibility: 'hidden',
    width: 'fit-content',
    borderRadius: 5,
    '&::after': {
      backgroundColor: 'transparent',
      borderRadius: 5,
      content: '""',
      inset: 0,
      pointerEvents: 'none',
      position: 'absolute',
      transition: 'background-color 180ms ease',
      zIndex: 2,
    },
    '&:hover::after': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
}));

const SkippedRowUnskipHint: React.FC<{ onUnskip: () => void }> = ({
  onUnskip,
}) => {
  const { classes } = useSkippedRowUnskipHintStyles();
  return (
    <Box className={classes.root}>
      <Box className={classes.field} data-skipped-row-unskip-hint>
        <ValueField
          value=""
          placeholder="Click to unskip"
          isEditing={false}
          onSave={() => undefined}
          onStartEditing={() => onUnskip()}
          onStopEditing={() => undefined}
          triggerOn="click"
          textAlign="left"
          fullWidth
          contentSized
          showHover={false}
          displayStyle={{ opacity: 0.5 }}
        />
      </Box>
    </Box>
  );
};

const useStatusCellStyles = makeStyles<{
  interactive: boolean;
  skippable: boolean;
  hoverColor: string;
}>()((_theme, { interactive, skippable, hoverColor }) => ({
  root: {
    alignSelf: 'stretch',
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    padding: '4px',
    pointerEvents: 'auto',
    ...(interactive ? { cursor: 'pointer' } : {}),
    ...(skippable
      ? {
          '&:hover [data-status-default-icon]': { display: 'none' },
          '&:hover [data-status-hover-icon]': { display: 'inline-flex' },
        }
      : {}),
  },
  defaultIcon: {
    alignItems: 'center',
    display: 'inline-flex',
    flexShrink: 0,
    justifyContent: 'center',
  },
  hoverIcon: {
    alignItems: 'center',
    color: hoverColor,
    display: 'none',
    flexShrink: 0,
    justifyContent: 'center',
  },
}));

/**
 * Right-click → Save/Update mapping (when the mapping differs from the stored
 * resource-code row) and Remove mapping, plus Skip/Unskip.
 * An unselected target is selected first so the menu and header match the row.
 */
const useCandidateRowContextMenu = (
  row: CandidateRow,
  status: CandidateRowStatus,
  onToggleDiscard: () => void,
  options?: {
    importedElement?: IElement;
    /** Clears product, conversion factor, and amount (DEV-3506). */
    onRemoveMapping?: () => void;
    isSelected?: boolean;
    onSelect?: (e: React.MouseEvent<HTMLElement>) => void;
    /**
     * True when this row also renders mapping persist/remove hover actions.
     * Save/Update mapping is omitted when the stored mapping is unchanged;
     * Remove mapping is omitted when {@link onRemoveMapping} is absent.
     */
    showSaveMapping?: boolean;
    onSuggestAiMapping?: () => void;
    aiMappingTargetCount?: number;
    aiMappingBusy?: boolean;
  },
) => {
  const importedElement = options?.importedElement;
  const onRemoveMapping = options?.onRemoveMapping;
  const isSelected = options?.isSelected;
  const onSelect = options?.onSelect;
  const showSaveMapping = options?.showSaveMapping === true;
  const onSuggestAiMapping = options?.onSuggestAiMapping;
  const aiMappingTargetCount = options?.aiMappingTargetCount ?? 0;
  const aiMappingBusy = options?.aiMappingBusy === true;
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const {
    canOpenSaveDialog,
    saveMappingAction,
    saveMappingTargetCount,
    matchingRowCount,
    handleSaveMapping,
  } = useCandidateSaveMapping({ row, importedElement });
  const canSaveFromMenu = showSaveMapping && canOpenSaveDialog;
  const position = useMenuPosition(row.id);
  const onContextMenu = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      // Keep a multi-selection when the target is already in it; otherwise
      // the menu would act on a row the header still treats as inactive.
      if (!isSelected) onSelect?.(e);
      const anchorPosition = menuMouseHandler(e);
      if (anchorPosition) openMenu({ anchorPosition, id: row.id });
    },
    [row.id, isSelected, onSelect],
  );
  const menu = (
    <>
      <Menu
        open={position !== undefined}
        anchorPosition={position}
        onClose={closeMenu}
      >
        {onSuggestAiMapping ? (
          <Menu.Item
            disabled={aiMappingBusy}
            icon={<Icon category="mui" icon="AutoAwesome" />}
            onClick={() => {
              closeMenu();
              onSuggestAiMapping();
            }}
          >
            {aiImportMappingMenuLabel(aiMappingTargetCount)}
          </Menu.Item>
        ) : null}
        {canSaveFromMenu && saveMappingAction ? (
          <Menu.Item
            onClick={() => {
              closeMenu();
              setSaveDialogOpen(true);
            }}
          >
            {saveMappingActionLabel(saveMappingAction, saveMappingTargetCount)}
          </Menu.Item>
        ) : null}
        {showSaveMapping && onRemoveMapping ? (
          <Menu.Item
            onClick={() => {
              closeMenu();
              onRemoveMapping();
            }}
          >
            Remove mapping
          </Menu.Item>
        ) : null}
        <Menu.Item
          onClick={() => {
            closeMenu();
            onToggleDiscard();
          }}
        >
          {isDiscardedStatus(status) ? 'Unskip row' : 'Skip row'}
        </Menu.Item>
      </Menu>
      {saveDialogOpen && canSaveFromMenu && saveMappingAction ? (
        <SaveMappingDialog
          onClose={() => setSaveDialogOpen(false)}
          matchingRowCount={matchingRowCount}
          targetCount={saveMappingTargetCount}
          action={saveMappingAction}
          onSave={handleSaveMapping}
        />
      ) : null}
    </>
  );
  return { onContextMenu, menu };
};

const useDelayedPointerTooltip = (enterDelayMs: number) => {
  const anchorRef = useRef<HTMLElement>(null);
  const enterTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const [open, setOpen] = useState(false);

  const close = useCallback(() => {
    if (enterTimerRef.current) {
      clearTimeout(enterTimerRef.current);
      enterTimerRef.current = undefined;
    }
    setOpen(false);
  }, []);

  const onPointerEnter = useCallback(() => {
    if (enterTimerRef.current) return;
    enterTimerRef.current = setTimeout(() => {
      enterTimerRef.current = undefined;
      setOpen(true);
    }, enterDelayMs);
  }, [enterDelayMs]);

  const onPointerLeave = useCallback(() => {
    close();
  }, [close]);

  useEffect(() => {
    if (!open) return;
    const onPointerMove = (e: PointerEvent) => {
      if (!anchorRef.current?.contains(e.target as Node)) close();
    };
    const onPointerDown = (e: PointerEvent) => {
      if (!anchorRef.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open, close]);

  useEffect(
    () => () => {
      if (enterTimerRef.current) clearTimeout(enterTimerRef.current);
    },
    [],
  );

  return { anchorRef, open, onPointerEnter, onPointerLeave, close };
};

const StatusCell: React.FC<{
  status?: CandidateRowStatus;
  onToggleDiscard?: () => void;
}> = ({ status, onToggleDiscard }) => {
  const interactive = Boolean(onToggleDiscard);
  const skippable =
    interactive && Boolean(status) && !isDiscardedStatus(status);
  const hoverColor = status ? STATUS_COLOR[status] : 'transparent';
  const { classes } = useStatusCellStyles({
    interactive,
    skippable,
    hoverColor,
  });
  const tooltip = useDelayedPointerTooltip(400);

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    tooltip.close();
    onToggleDiscard?.();
  };

  return (
    <Tooltip
      key={status}
      title={status ? CANDIDATE_STATUS_LABEL[status] : ''}
      open={tooltip.open}
      disableHoverListener
      disableFocusListener
      disableTouchListener
      disableInteractive
    >
      <Box
        ref={tooltip.anchorRef}
        className={classes.root}
        onClick={onToggleDiscard ? handleClick : undefined}
        onPointerEnter={tooltip.onPointerEnter}
        onPointerLeave={tooltip.onPointerLeave}
      >
        <Box
          className={classes.defaultIcon}
          data-status-default-icon
          sx={{
            color: status ? STATUS_COLOR[status] : 'transparent',
          }}
        >
          {status && STATUS_ICON[status]}
        </Box>
        {skippable && (
          <Box className={classes.hoverIcon} data-status-hover-icon>
            <Icon
              category="ui"
              icon="toggleOn"
              size={STATUS_ICON_OUTER_SIZE_PX}
            />
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

/** One file-header status chip: filter toggle + hover label. */
const StatusSummarySegment: React.FC<{
  label: string;
  hidden: boolean;
  color: string;
  count: number;
  icon: React.ReactNode;
  onToggle: () => void;
}> = ({ label, hidden, color, count, icon, onToggle }) => {
  const { classes, cx } = useStyles();
  // Same pointer-driven tooltip as StatusCell — MUI hover does not fire on
  // the absolutely positioned file-header cluster.
  const tooltip = useDelayedPointerTooltip(400);
  return (
    <Tooltip
      title={statusSummaryFilterTooltip(label, hidden)}
      open={tooltip.open}
      placement="top"
      disableHoverListener
      disableFocusListener
      disableTouchListener
      disableInteractive
    >
      <Box
        ref={tooltip.anchorRef}
        className={cx(classes.statusSummarySegment, {
          [classes.statusSummarySegmentHidden]: hidden,
        })}
        component="button"
        type="button"
        aria-pressed={hidden}
        onPointerEnter={tooltip.onPointerEnter}
        onPointerLeave={tooltip.onPointerLeave}
        onClick={(e) => {
          e.stopPropagation();
          tooltip.close();
          onToggle();
        }}
        sx={{
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            color: color,
            display: 'inline-flex',
            flexShrink: 0,
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
        <Typography component="span" sx={statusMetricTextSx}>
          <KineticNumberDisplay value={count} />
        </Typography>
      </Box>
    </Tooltip>
  );
};

/**
 * Status-summary segments. {@link STATUS_SUMMARY_GROUPS} defines grouping;
 * clicks toggle row visibility for that segment.
 */
const StatusSummary: React.FC<{
  counts: StatusCounts;
  hiddenGroups: ReadonlySet<StatusSummaryGroupKey>;
  onToggleGroup: (key: StatusSummaryGroupKey) => void;
}> = ({ counts, hiddenGroups, onToggleGroup }) => {
  const { classes } = useStyles();
  return (
    <Box
      className={classes.statusSummaryColumn}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
      }}
    >
      {STATUS_SUMMARY_GROUPS.map(({ key, statuses }) => {
        const count = statuses.reduce((sum, s) => sum + (counts[s] ?? 0), 0);
        if (!count) return null;
        return (
          <StatusSummarySegment
            key={key}
            label={CANDIDATE_STATUS_LABEL[key]}
            hidden={hiddenGroups.has(key)}
            color={STATUS_COLOR[key]}
            count={count}
            icon={STATUS_ICON[key]}
            onToggle={() => onToggleGroup(key)}
          />
        );
      })}
    </Box>
  );
};

const AnimatedCandidateRowSlot: React.FC<{
  visible: boolean;
  onExitComplete: () => void;
  alwaysVisible: boolean;
  children: React.ReactNode;
}> = ({ visible, onExitComplete, alwaysVisible, children }) => (
  <Collapse
    in={visible}
    timeout={STATUS_FILTER_ROW_COLLAPSE_MS}
    onExited={onExitComplete}
  >
    <HideOutsideViewport
      height={ELEMENT_LIST_ROW_HEIGHT}
      alwaysVisible={alwaysVisible || !visible}
      cullBeforeMount
    >
      {children}
    </HideOutsideViewport>
  </Collapse>
);

const ImportProgressStrip: React.FC<{
  resolved: number;
  total: number;
}> = ({ resolved, total }) => {
  const { classes } = useStyles();
  return (
    <Box
      className={classes.scrollStrip}
      sx={{
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        mr: 1,
      }}
    >
      <Typography component="span" sx={importProgressTextSx}>
        {formatImportProgressLabel(resolved, total)}
      </Typography>
    </Box>
  );
};

type IfcMeasureStripPicker = {
  row: CandidateRow;
  measures: readonly IfcMeasure[];
  active: IfcMeasure | undefined;
  onPick: (measure: IfcMeasure) => void;
  product?: IProduct;
  recipe?: Recipe;
};

const MetadataStrip: React.FC<{
  headers: string[];
  /** Omit to render the header labels rather than a row's values. */
  values?: Record<string, string | number>;
  /** When set, the Unit cell opens a menu of file-available measures. */
  ifcMeasurePicker?: IfcMeasureStripPicker;
}> = ({ headers, values, ifcMeasurePicker }) => {
  const { classes, cx } = useStyles();
  const scrollSync = React.useContext(ScrollSyncContext);
  const stripe = React.useContext(ImportStripeContext);
  const columnWidths = stripe?.columnWidths ?? [];
  const onResizePointerDown = stripe?.onResizePointerDown;
  const isHeaderRow = values === undefined;
  const showHandles = isHeaderRow && !!onResizePointerDown;
  const onSortHeader = isHeaderRow ? stripe?.onSortHeader : undefined;
  const activeSort = stripe?.sort;
  const quantityHeaderKey = stripe?.quantityHeader
    ? headerKey(stripe.quantityHeader)
    : undefined;
  const unitHeaderKey = stripe?.unitHeader
    ? headerKey(stripe.unitHeader)
    : undefined;
  const wasteHeaderKey = stripe?.wasteHeader
    ? headerKey(stripe.wasteHeader)
    : undefined;
  const [measureMenuAnchor, setMeasureMenuAnchor] =
    useState<HTMLElement | null>(null);
  return (
    <>
      <Box
        ref={scrollSync?.registerRef}
        onScroll={scrollSync?.onScroll}
        className={cx(
          classes.scrollStrip,
          isHeaderRow && classes.headerScrollStrip,
        )}
        sx={{
          display: 'flex',
          alignItems: 'center',
          flex: 1,
          gap: 0.5,
          mr: 1,
        }}
      >
        {headers.map((header, i) => {
          const w = columnWidths[i] ?? clampImportColumnWidthPx(48);
          const isQuantityColumn =
            !!quantityHeaderKey && headerKey(header) === quantityHeaderKey;
          const isUnitColumn =
            !!unitHeaderKey && headerKey(header) === unitHeaderKey;
          const isWasteColumn =
            !!wasteHeaderKey && headerKey(header) === wasteHeaderKey;
          let cellDisplay: string;
          let rawTooltip: string | undefined;
          if (values) {
            const raw = readKalkylfilRawCell(values, header);
            const wastePercent = isWasteColumn
              ? wasteCellToPercent(raw)
              : undefined;
            if (isWasteColumn && wastePercent !== undefined) {
              cellDisplay = `${getDisplayNumber(wastePercent)} %`;
              rawTooltip = raw.trim();
            } else if (isWasteColumn) {
              cellDisplay = formatImportTableCellDisplay(raw);
            } else if (
              isQuantityColumn &&
              shouldFormatImportTableCellAsNumber(raw)
            ) {
              const parsed = Number(removeNumberFormatting(raw.trim()));
              if (Number.isFinite(parsed)) {
                cellDisplay = getDisplayNumber(parsed);
                const trimmed = raw.trim();
                if (cellDisplay !== trimmed) rawTooltip = trimmed;
              } else {
                cellDisplay = formatImportTableCellDisplay(raw);
              }
            } else {
              cellDisplay = formatImportTableCellDisplay(raw);
            }
          } else {
            cellDisplay = stripe?.headerLabels?.[header] ?? header;
          }

          const headerLabel = stripe?.headerLabels?.[header] ?? header;
          const sortDir =
            isHeaderRow &&
            activeSort &&
            headerKey(activeSort.column) === headerKey(header)
              ? activeSort.dir
              : undefined;

          const canPickMeasure =
            !!values &&
            !!ifcMeasurePicker &&
            isUnitColumn &&
            ifcMeasurePicker.measures.length > 0;

          const handleStripCellClick = (
            e: React.MouseEvent<HTMLDivElement>,
          ) => {
            e.stopPropagation();
            onSortHeader?.(header);
          };

          const cellText = (
            <EllipsisText
              variant="caption"
              verticalAlign="middle"
              sx={{
                flex: 1,
                fontSize: 10,
                lineHeight: 1.4,
                minWidth: 0,
                color: values ? 'text.primary' : 'text.disabled',
                opacity: values ? 1 : 0.7,
                fontWeight: values ? 400 : 600,
                userSelect: values ? 'text' : 'none',
                textAlign:
                  isQuantityColumn || isWasteColumn ? 'right' : undefined,
              }}
            >
              {cellDisplay}
            </EllipsisText>
          );

          return (
            <Box
              key={`${header}_${i}`}
              className={classes.cell}
              onClick={onSortHeader ? handleStripCellClick : undefined}
              sx={mergeSx(
                { width: w, minWidth: w },
                onSortHeader ? sortableHeaderCursorSx : undefined,
              )}
            >
              <Tooltip
                title={
                  onSortHeader ? `Sort by ${headerLabel}` : (rawTooltip ?? '')
                }
                placement="top"
                disableInteractive
                enterDelay={600}
              >
                {canPickMeasure ? (
                  <button
                    type="button"
                    className={classes.measurePickHit}
                    data-open={measureMenuAnchor ? '' : undefined}
                    onClick={(e) => {
                      e.stopPropagation();
                      setMeasureMenuAnchor(e.currentTarget);
                    }}
                  >
                    {cellText}
                  </button>
                ) : (
                  cellText
                )}
              </Tooltip>
              {sortDir ? (
                <Icon
                  category="mui"
                  icon={
                    sortDir === 'asc' ? 'KeyboardArrowDown' : 'KeyboardArrowUp'
                  }
                  size={10}
                  color="text.disabled"
                />
              ) : null}
              {showHandles && onResizePointerDown ? (
                <Tooltip
                  title="Resize"
                  placement="top"
                  disableInteractive
                  enterDelay={600}
                >
                  <Box
                    className={classes.resizeHandle}
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      onResizePointerDown(i)(e);
                    }}
                  />
                </Tooltip>
              ) : null}
            </Box>
          );
        })}
      </Box>
      {ifcMeasurePicker ? (
        <Menu
          open={measureMenuAnchor !== null}
          anchorEl={measureMenuAnchor}
          onClose={() => setMeasureMenuAnchor(null)}
          width={200}
        >
          <Menu.Group label={IFC_MEASURE_MENU_GROUP_LABEL}>
            {measureMenuAnchor !== null &&
              ifcMeasurePicker.measures.map((measure) => {
                const { columnLabel, value } = getIfcMeasureMenuParts(
                  ifcMeasurePicker.row,
                  measure,
                );
                const canAutoImport = ifcMeasureHasAutoConversion(
                  ifcMeasurePicker.row,
                  measure,
                  ifcMeasurePicker.product,
                  ifcMeasurePicker.recipe,
                );
                return (
                  <Menu.Item
                    key={measure}
                    selected={measure === ifcMeasurePicker.active}
                    searchText={formatIfcMeasureMenuLabel(
                      ifcMeasurePicker.row,
                      measure,
                    )}
                    icon={
                      canAutoImport ? (
                        <Icon category="mui" icon="Check" />
                      ) : undefined
                    }
                    onClick={() => {
                      setMeasureMenuAnchor(null);
                      ifcMeasurePicker.onPick(measure);
                    }}
                  >
                    {columnLabel}
                    {value ? (
                      <Menu.Item.RightIcon>{value}</Menu.Item.RightIcon>
                    ) : null}
                  </Menu.Item>
                );
              })}
          </Menu.Group>
        </Menu>
      ) : null}
    </>
  );
};

const useResizableColumnWidths = (
  importId: string,
  headers: readonly string[],
  sampleRows: readonly Record<string, string | number>[],
  headerLabels: Record<string, string>,
): {
  columnWidths: number[];
  onResizePointerDown: (
    columnIndex: number,
  ) => React.PointerEventHandler<HTMLDivElement>;
} => {
  const autoWidths = useMemo(
    () => computeImportColumnWidths(headers, sampleRows, { headerLabels }),
    [headers, sampleRows, headerLabels],
  );
  const [persisted, setPersisted] = useState<number[] | undefined>(() =>
    loadImportColumnWidths(importId),
  );
  // Reset persisted state when switching imports (the hook is reused per
  // section, so the importId change must re-seed from storage).
  const lastIdRef = useRef(importId);
  if (lastIdRef.current !== importId) {
    lastIdRef.current = importId;
    setPersisted(loadImportColumnWidths(importId));
  }

  type DragState = {
    anchorX: number;
    colIndex: number;
    delta: number;
    startWidths: number[];
  };
  const [drag, setDrag] = useState<DragState | null>(null);
  const dragRef = useRef<DragState | null>(null);
  dragRef.current = drag;
  const tracking = drag !== null;

  const baseWidths = useMemo(() => {
    if (persisted && persisted.length === headers.length) return persisted;
    return autoWidths;
  }, [persisted, autoWidths, headers.length]);

  const columnWidths = useMemo(() => {
    if (!drag) return baseWidths;
    return drag.startWidths.map((w, i) =>
      i === drag.colIndex ? clampImportColumnWidthPx(w + drag.delta) : w,
    );
  }, [drag, baseWidths]);

  useEffect(() => {
    if (!tracking) return;
    const onMove = (e: PointerEvent) =>
      setDrag((d) => (d ? { ...d, delta: e.clientX - d.anchorX } : null));
    const onUp = () => {
      const d = dragRef.current;
      setDrag(null);
      if (!d) return;
      if (Math.abs(d.delta) > 2) {
        const suppressClick = (e: Event) => e.stopPropagation();
        window.addEventListener('click', suppressClick, {
          capture: true,
          once: true,
        });
        setTimeout(
          () => window.removeEventListener('click', suppressClick, true),
          200,
        );
      }
      const finalWidths = d.startWidths.map((w, i) =>
        i === d.colIndex ? clampImportColumnWidthPx(w + d.delta) : w,
      );
      setPersisted(finalWidths);
      saveImportColumnWidths(importId, finalWidths);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    const prevSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      document.body.style.userSelect = prevSelect;
    };
  }, [tracking, importId]);

  const onResizePointerDown = useCallback(
    (columnIndex: number): React.PointerEventHandler<HTMLDivElement> =>
      (event) => {
        event.preventDefault();
        event.stopPropagation();
        setDrag({
          anchorX: event.clientX,
          colIndex: columnIndex,
          delta: 0,
          startWidths: [...columnWidths],
        });
      },
    [columnWidths],
  );

  return { columnWidths, onResizePointerDown };
};

type ImportedElementsByRowId = ReturnType<typeof collectImportedElements>;

type CandidateRowListItemProps = {
  row: CandidateRow;
  orderedHeaders: readonly string[];
  wasteScale: WasteScale | undefined;
  mappingsByCode: MappingsByCode;
  discardTriggerByRowId: Record<string, DiscardTrigger>;
  /** Stashed elements for discarded rows, so they keep showing their mapping. */
  stashedElements: ReadonlyMap<string, IElement>;
  productsById: ProductsById;
  recipesById: RecipesById;
  recipes: Recipe[];
  importedElements: ImportedElementsByRowId;
  importsById: Record<string, ProjectElementImport | undefined>;
  /**
   * Status precomputed once per section (no per-row draft overlay). Reused as-is
   * unless the row carries a live draft, so a row mount skips the
   * status-materialize (a full baseline build) the section already paid for.
   */
  sectionStatus: CandidateRowStatus | undefined;
  /** Part of the multi-selection (DEV-3456); holds candidate row ids. */
  isMultiSelected: boolean;
  selectedBlockPosition: SelectedBlockPosition;
  bulk: BulkRowActions;
  aiMapping: AiImportMappingActions;
  onSelectRow: (id: string, e: React.MouseEvent) => void;
  onGoToElement: (elementId: string) => void;
  onDiscardRow: (rowId: string) => void;
  onSwitchMapping: (
    row: CandidateRow,
    element: IElement,
    id: string,
    type: MappingReferenceType,
  ) => void;
  /** Clear a materialized row's mapping: remove the element, reset the row. */
  onUnsetMapping: (row: CandidateRow, element: IElement) => void;
};

type ImportedRowItemProps = {
  row: CandidateRow;
  element: IElement;
  /**
   * True when {@link element} is the live element in the project tree; false when
   * it's only a stashed copy (discarded row). Element actions (delete/duplicate/
   * enable/add/rename) are only valid when the element is actually in the project.
   */
  elementInProject: boolean;
  orderedHeaders: readonly string[];
  mappingsByCode: MappingsByCode;
  recipes: Recipe[];
  status: CandidateRowStatus;
  isSelected: boolean;
  selectedBlockPosition: SelectedBlockPosition;
  /** Fans an edit on this row out to the whole multi-selection (DEV-3456). */
  bulk: BulkRowActions;
  aiMapping: AiImportMappingActions;
  onSelectRow: (id: string, e: React.MouseEvent) => void;
  onGoToElement: (elementId: string) => void;
  onToggleDiscard: () => void;
  /** Re-map this row's element to a different product or recipe (DEV-3369/3387). */
  onSwitchMapping: (
    row: CandidateRow,
    element: IElement,
    id: string,
    type: MappingReferenceType,
  ) => void;
  /** Clear this row's mapping: remove the element, reset the row (DEV-3506). */
  onUnsetMapping: (row: CandidateRow, element: IElement) => void;
};

/**
 * Materialized row: reuse the Elements-tab editing surface (name / quantity
 * expression+override / unit menu) via {@link useElementRowEditing}. Trailing
 * slot + kebab stay csv-import-specific (status cell, CSV metadata strip,
 * "Go to element" kebab).
 */
const ImportedRowListItem: React.FC<ImportedRowItemProps> = ({
  row,
  element,
  elementInProject,
  orderedHeaders,
  mappingsByCode,
  recipes,
  status,
  isSelected,
  selectedBlockPosition,
  bulk,
  aiMapping,
  onSelectRow,
  onGoToElement,
  onToggleDiscard,
  onSwitchMapping,
  onUnsetMapping,
}) => {
  const editing = useImportedRowEditing({
    row,
    element,
    bulk,
  });
  const rowHandle = useRef<ElementListRowHandle>(null);
  const { classes } = useStyles();
  // Generic product / recipe the row is mapped to — seeds the in-row picker so a
  // mapped row offers the same mapping editor as an incomplete candidate row.
  const mappedProductId = resolveMappedRowProductId(
    element,
    row,
    mappingsByCode,
  );
  const mappedRecipeId = resolveMappedRowRecipeId(element, row, mappingsByCode);
  const mappedKey = mappedRecipeId ?? mappedProductId;
  const handleKebabClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      onGoToElement(element.id);
    },
    [element.id, onGoToElement],
  );
  // In the imports tab the normal element actions (delete/duplicate/enable/add/
  // rename) don't make sense — a row maps to a product, and a discarded row has
  // no live element at all. Right-click: Save/Remove mapping + Skip/Unskip.
  const restoreContextMenu = useCandidateRowContextMenu(
    row,
    status,
    onToggleDiscard,
    {
      importedElement: element,
      onRemoveMapping: isDiscardedStatus(status)
        ? undefined
        : () => onUnsetMapping(row, element),
      isSelected,
      onSelect: (e) => onSelectRow(row.id, e),
      showSaveMapping: true,
      onSuggestAiMapping:
        aiMapping.targetCountFromRow(row) > 0
          ? () => aiMapping.mapFromRow(row)
          : undefined,
      aiMappingTargetCount: aiMapping.targetCountFromRow(row),
      aiMappingBusy: aiMapping.isMapping(row.id),
    },
  );
  const handlePickMapping = useCallback(
    (id: string | undefined, type: MappingReferenceType) => {
      // Cleared the picker → drop the mapping, remove the element (DEV-3506).
      if (!id) {
        onUnsetMapping(row, element);
        return;
      }
      // Same target → nothing to re-map; only a different entity rebuilds the row.
      if (id === mappedKey) return;
      onSwitchMapping(row, element, id, type);
    },
    [mappedKey, onSwitchMapping, onUnsetMapping, row, element],
  );
  const isDiscarded = isDiscardedStatus(status);
  return (
    <Box
      className={classes.candidateRow}
      data-discarded={isDiscarded ? '' : undefined}
      data-selected={isSelected ? '' : undefined}
    >
      <ElementListRow
        ref={rowHandle}
        depth={2}
        nameOverrideSlot={
          isDiscarded ? (
            <SkippedRowUnskipHint onUnskip={onToggleDiscard} />
          ) : (
            <MappingPicker
              selectedKey={mappedKey}
              recipes={recipes}
              placeholder="Map to product or recipe"
              searchPlaceholder="Search for a product or recipe..."
              onPick={handlePickMapping}
              isRowSelected={isSelected}
            />
          )
        }
        categoryIcon={editing.categoryIcon ?? 'unknownproduct'}
        quantityValue={editing.quantityValue}
        quantityDisplayValue={editing.quantityDisplayValue}
        quantityDisplayUnit={editing.quantityDisplayUnit}
        quantityPlaceholder={editing.quantityPlaceholder}
        unit={editing.unit}
        onQtySave={editing.handleQtySave}
        onQtyFxClick={editing.onQtyFxClick}
        onUnitClick={editing.onUnitClick}
        disabled={editing.isDeactivated}
        hideMappingColumns={isDiscarded}
        selected={isSelected}
        selectedBlockPosition={selectedBlockPosition}
        onSelect={(e) => onSelectRow(row.id, e)}
        onContextMenu={restoreContextMenu.onContextMenu}
        nameTrailingSlot={
          elementInProject ? (
            <Tooltip title="Go to element" placement="top" disableInteractive>
              <Box
                className={classes.goToElementAction}
                data-go-to-element-action
                component="button"
                onClick={handleKebabClick}
                sx={{
                  padding: '2px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  border: 'none',
                  background: 'none',
                  color: 'rgba(0,0,0,0.5)',
                  flexShrink: 0,
                  '&:hover': { color: 'rgba(0,0,0,0.8)' },
                }}
              >
                <Icon category="mui" icon="ArrowForward" size={12} />
              </Box>
            </Tooltip>
          ) : null
        }
        kebabLeadingSlot={
          <StatusCell status={status} onToggleDiscard={onToggleDiscard} />
        }
        kebabLeadingInteractive
        trailingSlot={
          <>
            <CandidateSaveMappingButton row={row} importedElement={element} />
            <Box
              sx={{
                display: 'flex',
                flex: 1,
                minWidth: 0,
                alignSelf: 'stretch',
                opacity: isDiscardedStatus(status) ? 0.5 : 1,
              }}
            >
              <MetadataStrip
                headers={[...orderedHeaders]}
                values={rawWithActiveIfcQuantity(row, editing.activeIfcMeasure)}
                ifcMeasurePicker={
                  editing.fileMeasures.length > 0
                    ? {
                        row,
                        measures: editing.fileMeasures,
                        active: editing.activeIfcMeasure,
                        onPick: editing.onPickIfcMeasure,
                        product: editing.isRecipeMapping
                          ? undefined
                          : editing.product,
                        recipe: editing.isRecipeMapping
                          ? editing.recipe
                          : undefined,
                      }
                    : undefined
                }
              />
            </Box>
          </>
        }
      />
      {editing.unitMenu}
      {restoreContextMenu.menu}
    </Box>
  );
};

type IncompleteRowItemProps = {
  row: CandidateRow;
  orderedHeaders: readonly string[];
  wasteScale: WasteScale | undefined;
  productsById: ProductsById;
  recipesById: RecipesById;
  recipes: Recipe[];
  status: CandidateRowStatus;
  isSelected: boolean;
  selectedBlockPosition: SelectedBlockPosition;
  /** Fans an edit on this row out to the whole multi-selection (DEV-3456). */
  bulk: BulkRowActions;
  aiMapping: AiImportMappingActions;
  onSelectRow: (id: string, e: React.MouseEvent) => void;
  onToggleDiscard: () => void;
};

/**
 * Not-yet-materialized row: in-row mapping editor inspired by the Elements
 * tab. Click the name slot to open the product picker. Quantity slot stores
 * `factor = qtyTo / qtyFrom` on the per-row draft (on the project); the DB
 * mapping is only written via "Save mapping" on row hover. Unit slot opens a
 * menu of the product's available conversion units.
 */
const IncompleteRowListItem: React.FC<IncompleteRowItemProps> = ({
  row,
  orderedHeaders,
  wasteScale,
  productsById,
  recipesById,
  recipes,
  status,
  isSelected,
  selectedBlockPosition,
  bulk,
  aiMapping,
  onSelectRow,
  onToggleDiscard,
}) => {
  const { classes } = useStyles();
  const [unitMenuAnchor, setUnitMenuAnchor] = useState<HTMLElement | null>(
    null,
  );
  // Draft write + import in one batch — a single undo frame (DEV-3457).
  const { importRowWithDraft, unsetImportRow } = useAutoImportCandidates();

  // Per-row draft (on the project): the full in-row mapping (product, unit, rate)
  // lives here, never on the shared DB mapping — so editing one row never leaks
  // into another row with the same resurskod. Non-materialized rows are driven
  // solely by this draft; `mappingsByCode` is empty here (see EMPTY_MAPPINGS).
  const draft = useImportRowDraft(row.id);
  const contextMenu = useCandidateRowContextMenu(row, status, onToggleDiscard, {
    onRemoveMapping: importRowDraftHasRemovableMapping(draft)
      ? () => unsetImportRow(row)
      : undefined,
    isSelected,
    onSelect: (e) => onSelectRow(row.id, e),
    // Incomplete rows only show Save mapping on discarded/skipped rows.
    showSaveMapping: isDiscardedStatus(status),
    onSuggestAiMapping: isDiscardedStatus(status)
      ? undefined
      : () => aiMapping.mapFromRow(row),
    aiMappingTargetCount: aiMapping.targetCountFromRow(row),
    aiMappingBusy: aiMapping.isMapping(row.id),
  });
  const pinnedIfcMeasure = useMemo(
    () => resolveActiveIfcMeasure(row, draft.ifcMeasure),
    [row, draft.ifcMeasure],
  );
  const effectiveRow = useMemo(
    () => rowWithActiveIfcMeasure(row, draft.ifcMeasure),
    [row, draft.ifcMeasure],
  );
  const fileMeasures = useMemo(() => listAvailableIfcMeasures(row), [row]);
  const unitFrom = candidateUnitFrom(effectiveRow) ?? '';

  const draftMapping = useMemo(
    () => (unitFrom ? draftToOriginMapping(draft, unitFrom) : undefined),
    [draft, unitFrom],
  );
  const effectiveMapping = draftMapping;
  const referenceId =
    effectiveMapping?.referenceType === 'product'
      ? effectiveMapping.referenceId
      : undefined;
  const recipeReferenceId =
    effectiveMapping?.referenceType === 'recipe'
      ? effectiveMapping.referenceId
      : undefined;

  const mappedProduct = useMemo(
    () =>
      resolveCandidateProduct(
        effectiveRow,
        EMPTY_MAPPINGS,
        productsById,
        draftMapping,
      ),
    [effectiveRow, productsById, draftMapping],
  );
  const mappedRecipe = useMemo(
    () =>
      resolveCandidateRecipe(
        effectiveRow,
        EMPTY_MAPPINGS,
        recipesById,
        draftMapping,
      ),
    [effectiveRow, recipesById, draftMapping],
  );
  // Generic-product lookup is by referenceId regardless of whether the row
  // is fully resolvable yet; productsById is empty for generic products, so
  // walk the generic store via resolveCandidateProduct first and fall back
  // to a direct id lookup so the picker reflects the saved choice.
  const pickedProduct: IProduct | undefined =
    mappedProduct ?? (referenceId ? productsById[referenceId] : undefined);
  const pickedRecipe: Recipe | undefined =
    mappedRecipe ??
    (recipeReferenceId ? recipesById[recipeReferenceId] : undefined);
  // Discriminate on the draft's referenceType, not on recipe-lookup success, so
  // the row stays recipe-mapped even before recipesById has loaded the recipe.
  const referenceType: MappingReferenceType =
    effectiveMapping?.referenceType === 'recipe' ? 'recipe' : 'product';
  const isRecipeMapping = referenceType === 'recipe';

  const elementDisplay = useMemo(
    () =>
      getCandidateRowElementDisplay({
        row,
        product: pickedProduct,
        recipe: pickedRecipe,
        mappings: EMPTY_MAPPINGS,
        importedElement: undefined,
        wasteScale,
        draftMapping,
        selectedQuantity: draft.selectedQuantity,
        ifcMeasure: draft.ifcMeasure,
      }),
    [
      row,
      pickedProduct,
      pickedRecipe,
      wasteScale,
      draftMapping,
      draft.selectedQuantity,
      draft.ifcMeasure,
    ],
  );

  const existingEntry = unitFrom
    ? effectiveMapping?.mappings[unitFrom]
    : undefined;
  const autoConversion = pickedProduct
    ? candidateConversion(effectiveRow, pickedProduct)
    : undefined;
  // The column this row will import through — the pick when the target can
  // express it, otherwise whichever one it can (see `resolveImportQuantity`).
  const activeIfcMeasure = useMemo(() => {
    const target: ImportTarget | undefined = isRecipeMapping
      ? pickedRecipe && { kind: 'recipe', recipe: pickedRecipe }
      : pickedProduct && { kind: 'product', product: pickedProduct };
    if (!target) return pinnedIfcMeasure;

    const resolution = resolveImportQuantity({
      row,
      target,
      pinnedMeasure: pinnedIfcMeasure,
      explicitQuantity: draft.selectedQuantity,
      manualFactor: existingEntry?.quantityFactor,
    });
    if (resolution.status === 'unresolvable') return pinnedIfcMeasure;
    return resolution.measure ?? pinnedIfcMeasure;
  }, [
    row,
    pinnedIfcMeasure,
    isRecipeMapping,
    pickedRecipe,
    pickedProduct,
    draft.selectedQuantity,
    existingEntry?.quantityFactor,
  ]);
  // The user picks an element quantity (e.g. "Side area"); its unit drives the
  // in-row display. Falls back to the saved entry / product auto-conversion.
  const selectedQuantity = draft.selectedQuantity;
  const selectedQuantityUnit = selectedQuantity
    ? elementQuantityUnitRecord[selectedQuantity]
    : undefined;
  // A recipe row resolves its quantity in materialization, not in the draft, so
  // surface a unit for it: the ghost's resolved one when it materialized, else the
  // recipe category's default dimension — so the selector is never blank.
  const recipeDefaultQuantity = pickedRecipe
    ? getElementCategory(pickedRecipe.category_id)?.defaultSelectedQuantity
    : undefined;
  const recipeUnitTo = isRecipeMapping
    ? elementDisplay?.unit ||
      (recipeDefaultQuantity
        ? elementQuantityUnitRecord[recipeDefaultQuantity]
        : undefined)
    : undefined;
  // The unit the product row will materialize into — a product maps to an element
  // quantity (e.g. a door → `area_side`/m²), not its native `unit` (kg). Showing
  // this before any input keeps the unit stable through materialization, so the
  // typed value lands in the unit the user saw (DEV-3439).
  const productDefaultQuantity = useMemo(
    () =>
      isRecipeMapping
        ? undefined
        : getProductDefaultQuantityName(pickedProduct, effectiveRow),
    [isRecipeMapping, pickedProduct, effectiveRow],
  );
  const productDefaultUnit = productDefaultQuantity
    ? elementQuantityUnitRecord[productDefaultQuantity]
    : undefined;
  // Draft pick wins; otherwise the implied quantity behind the unit cell
  // (m² → area_top vs area_side) so the menu marks which one is active.
  const highlightedQuantity =
    selectedQuantity ??
    (isRecipeMapping ? recipeDefaultQuantity : productDefaultQuantity);
  const effectiveUnitTo =
    selectedQuantityUnit ??
    existingEntry?.unitTo ??
    // The materialized quantity unit wins over the product's native conversion
    // unit: `autoConversion?.unit` is the product unit (e.g. kg), which is the
    // very mismatch DEV-3439 fixes when the element measures in another quantity.
    productDefaultUnit ??
    autoConversion?.unit ??
    pickedProduct?.unit ??
    recipeUnitTo ??
    '';

  const qtyToValue = inverseQtyToFromEntry({
    row: effectiveRow,
    entry: existingEntry,
  });

  // Left Amount (Mängd) only shows a resolved Nodon value — never the file
  // quantity number. File Quantity/Unit on the strip is the mapper input;
  // when units mismatch the amount stays empty until omräkningsfaktor / typed
  // total (DEV-3544: avoid 74 m looking like 74 kg after a product pick).
  const typedTotalNumber = draft.totalQuantity;
  const typedTotal =
    typedTotalNumber !== undefined ? String(typedTotalNumber) : undefined;
  const resolvedDisplay =
    qtyToValue !== undefined
      ? getDisplayNumber(qtyToValue)
      : elementDisplay?.quantityValue;
  const quantityFieldValue = typedTotal;
  const quantityDisplayValue =
    typedTotalNumber !== undefined && Number.isFinite(typedTotalNumber)
      ? getDisplayNumber(typedTotalNumber)
      : undefined;
  const quantityPlaceholderValue =
    typedTotal === undefined ? resolvedDisplay : undefined;

  // Element quantities the user can map to — for a product, the ones it can
  // convert to; for a recipe, the ones its category defines (DEV-3387).
  const quantityOptions = useMemo(
    () =>
      isRecipeMapping
        ? getSelectableQuantityNamesForRecipe(pickedRecipe)
        : getSelectableQuantityNames(pickedProduct),
    [isRecipeMapping, pickedRecipe, pickedProduct],
  );

  const onPickMapping = useCallback(
    (id: string | undefined, type: MappingReferenceType) => {
      if (!unitFrom) return;
      if (id && bulk.shouldFanOut(row.id)) {
        bulk.setMapping(id, type);
        return;
      }
      if (!id) {
        // Cleared the picker → drop the whole in-row mapping and revert to a
        // bare candidate (the candidate row stays; DEV-3506).
        unsetImportRow(row);
        return;
      }
      // Keep the row's calibration when the new target can still address the
      // quantity it was calibrated for (wall → wall keeps its m² rate); drop it
      // only when it has become meaningless. Materialization then re-resolves:
      // a recognizable unit auto-resolves (kg→Mass, m³→Volume), an ambiguous one
      // waits for a quantity pick, a non-physical one for a typed Mängd.
      const keepsCalibration = isMappingCalibrationCompatible({
        entry: { unitTo: draft.unitTo, quantityName: draft.selectedQuantity },
        referenceType: type,
        product: type === 'product' ? productsById[id] : undefined,
        recipe: type === 'recipe' ? recipesById[id] : undefined,
      });
      importRowWithDraft(row, {
        referenceId: id,
        referenceType: type,
        ...(keepsCalibration
          ? {}
          : {
              // Settle the quantity here, at the pick, rather than letting every
              // consumer re-derive it: the unit shown while the user types the
              // Amount or the rate must be the unit the value ends up in. A
              // later re-resolution that disagreed would move the unit under an
              // already-submitted value (DEV-3575).
              selectedQuantity:
                type === 'product'
                  ? getProductDefaultQuantityName(
                      productsById[id],
                      effectiveRow,
                    )
                  : undefined,
              quantityFactor: undefined,
              unitTo: undefined,
              totalQuantity: undefined,
              expression: undefined,
            }),
      });
    },
    [
      row,
      effectiveRow,
      unitFrom,
      bulk,
      draft.unitTo,
      draft.selectedQuantity,
      productsById,
      recipesById,
      importRowWithDraft,
      unsetImportRow,
    ],
  );

  const onQtySave = useCallback(
    (value: number | undefined) => {
      if (!unitFrom) return;
      if (value !== undefined && bulk.shouldFanOut(row.id)) {
        bulk.setTotalQuantity(value, row.id);
        return;
      }
      switch (referenceType) {
        case 'recipe': {
          // The typed total maps straight into the selected element quantity (no
          // product conversion). Keep the factor so the row stays materializable.
          const qtyFrom = candidateQuantityFrom(effectiveRow);
          const factor = value !== undefined && qtyFrom ? value / qtyFrom : 1;
          importRowWithDraft(row, {
            referenceId: recipeReferenceId ?? pickedRecipe?.id,
            referenceType: 'recipe',
            selectedQuantity: selectedQuantity ?? undefined,
            quantityFactor: factor,
            totalQuantity: value,
            expression: undefined,
          });
          break;
        }
        case 'product': {
          if (!pickedProduct) return;
          // A typed total always recalibrates the factor — against the picked
          // quantity's unit, else the product's default quantity — so total and
          // conversion factor never decouple (DEV-3501).
          const derived =
            value === undefined
              ? selectedQuantity
                ? deriveQuantityMappingEntry({
                    row: effectiveRow,
                    product: pickedProduct,
                    quantityName: selectedQuantity,
                  })
                : {
                    unitTo: (effectiveUnitTo || undefined) as
                      ConversionFactorUnit | undefined,
                    quantityFactor: undefined,
                  }
              : deriveTypedTotalMappingEntry({
                  row: effectiveRow,
                  product: pickedProduct,
                  qtyTo: value,
                  selectedQuantity,
                  fallbackUnitTo: effectiveUnitTo,
                });
          if (!derived) return;
          // Typing a number directly replaces any rate-driven formula. Re-seed
          // the product reference so the draft is a complete overlay even when
          // the product originally came from a DB mapping (no draft referenceId).
          importRowWithDraft(row, {
            referenceId: pickedProduct.id,
            referenceType: 'product',
            unitTo: derived.unitTo,
            quantityFactor: derived.quantityFactor,
            totalQuantity: value,
            expression: undefined,
          });
          break;
        }
        default:
          return assertUnreachable(referenceType);
      }
    },
    [
      row,
      effectiveRow,
      referenceType,
      pickedRecipe,
      recipeReferenceId,
      pickedProduct,
      unitFrom,
      effectiveUnitTo,
      selectedQuantity,
      bulk,
      importRowWithDraft,
    ],
  );

  const onPickQuantity = useCallback(
    (name: ElementQuantityExpressionName) => {
      setUnitMenuAnchor(null);
      if (!unitFrom) return;
      if (bulk.shouldFanOut(row.id)) {
        bulk.setQuantityName(name);
        return;
      }
      switch (referenceType) {
        case 'recipe':
          // Steer which element quantity the Mängd fills. The factor stays as-is
          // — materialization auto-derives it from the CSV unit for the picked
          // quantity (e.g. m²→Side area = 1); a typed Mängd later overrides it.
          importRowWithDraft(row, {
            referenceId: recipeReferenceId ?? pickedRecipe?.id,
            referenceType: 'recipe',
            selectedQuantity: name,
            quantityFactor: existingEntry?.quantityFactor,
            unitTo: elementQuantityUnitRecord[name] as
              ConversionFactorUnit | undefined,
          });
          break;
        case 'product': {
          if (!pickedProduct) return;
          // Calibrate the factor for the picked quantity, carrying a user-typed
          // total when one exists; auto-convertible products derive it from the
          // product factors so the value is shown in the picked unit immediately.
          const derived = deriveQuantityMappingEntry({
            row: effectiveRow,
            product: pickedProduct,
            quantityName: name,
            typedTotal: draft.totalQuantity,
            // Re-express a prior CSV->nodon factor in the picked unit (e.g. st->kg
            // becomes st->m²) so switching quantity keeps the calibrated ratio.
            fromEntry: existingEntry,
          });
          if (!derived) return;
          // Re-seed the product reference so the draft fully overlays any DB
          // mapping; without it draftToOriginMapping returns undefined and the
          // pick is silently dropped for DB-mapped rows.
          importRowWithDraft(row, {
            referenceId: pickedProduct.id,
            referenceType: 'product',
            selectedQuantity: name,
            unitTo: derived.unitTo,
            quantityFactor: derived.quantityFactor,
          });
          break;
        }
        default:
          return assertUnreachable(referenceType);
      }
    },
    [
      row,
      effectiveRow,
      referenceType,
      pickedRecipe,
      recipeReferenceId,
      pickedProduct,
      unitFrom,
      draft.totalQuantity,
      existingEntry,
      bulk,
      importRowWithDraft,
    ],
  );

  const onPickIfcMeasure = useCallback(
    (measure: IfcMeasure) => {
      if (bulk.shouldFanOut(row.id)) {
        bulk.setIfcMeasure(measure);
        return;
      }
      importRowWithDraft(
        row,
        importRowDraftPatchForIfcMeasure(
          row,
          measure,
          isRecipeMapping ? undefined : pickedProduct,
          isRecipeMapping ? pickedRecipe : undefined,
        ),
      );
    },
    [
      bulk,
      importRowWithDraft,
      isRecipeMapping,
      pickedProduct,
      pickedRecipe,
      row,
    ],
  );

  // Prefer the ghost element's icon; before it materializes (e.g. a recipe whose
  // unit still needs disambiguation), fall back to the picked product / recipe
  // category so the row never shows ElementListRow's default 'slab' icon.
  const categoryIcon =
    elementDisplay?.iconName ??
    (pickedProduct
      ? getProductCategoryIconName(pickedProduct.id)
      : pickedRecipe
        ? getElementIconCategoryName(pickedRecipe.category_id)
        : undefined);

  // A row has a target once either a product or a recipe is picked.
  const hasPickedMapping = Boolean(pickedProduct || pickedRecipe);

  const { selectedIds } = useListSelection();
  const onEmptyActivate = useCallback(
    ({ isRowSelected }: { isRowSelected: boolean | undefined }) => {
      if (hasPickedMapping) return false;
      if (aiMapping.isMapping(row.id)) return true;
      // First click on this row with nothing else selected → AI. Clicking
      // the placeholder on another row while a selection exists opens the
      // picker instead of mapping with AI.
      if (!isRowSelected && selectedIds.size === 0) {
        aiMapping.mapFromRow(row);
        return true;
      }
      return false;
    },
    [aiMapping, hasPickedMapping, row, selectedIds],
  );

  const isAiMapping = !hasPickedMapping && aiMapping.isMapping(row.id);
  const [isPickerEditing, setIsPickerEditing] = useState(false);

  const isDiscarded = isDiscardedStatus(status);

  return (
    <Box
      className={classes.candidateRow}
      data-discarded={isDiscarded ? '' : undefined}
      data-selected={isSelected ? '' : undefined}
    >
      <ElementListRow
        depth={2}
        hideMappingColumns={isDiscarded}
        nameOverrideSlot={
          isDiscarded ? (
            <SkippedRowUnskipHint onUnskip={onToggleDiscard} />
          ) : (
            <Box
              sx={{
                display: 'flex',
                flex: 1,
                minWidth: 0,
                alignItems: 'center',
              }}
            >
              <Box
                className={
                  isAiMapping ? classes.mappingPickerHidden : undefined
                }
                sx={{
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <MappingPicker
                  selectedKey={recipeReferenceId ?? referenceId}
                  recipes={recipes}
                  placeholder={
                    hasPickedMapping
                      ? 'Map to product or recipe'
                      : 'Select product or recipe'
                  }
                  searchPlaceholder="Search for a product or recipe..."
                  onPick={onPickMapping}
                  isRowSelected={isSelected}
                  shrinkWhenIdle={!isPickerEditing}
                  onEmptyActivate={onEmptyActivate}
                  onEditingChange={setIsPickerEditing}
                />
              </Box>
              {!isPickerEditing ? (
                <CandidateAiMappingButton row={row} aiMapping={aiMapping} />
              ) : null}
            </Box>
          )
        }
        categoryIcon={categoryIcon}
        iconSlot={
          !hasPickedMapping && !elementDisplay ? (
            <Box
              sx={{
                width: 12,
                height: 12,
                flexShrink: 0,
              }}
            />
          ) : undefined
        }
        hideQuantity={!hasPickedMapping && !elementDisplay}
        quantityValue={quantityFieldValue}
        quantityDisplayValue={quantityDisplayValue}
        quantityDisplayUnit={effectiveUnitTo}
        quantityPlaceholder={quantityPlaceholderValue}
        unit={effectiveUnitTo}
        quantityReadonly={!hasPickedMapping}
        onQtySave={onQtySave}
        onUnitClick={
          quantityOptions.length > 0
            ? (e) => {
                e.stopPropagation();
                if (!isSelected) onSelectRow(row.id, e);
                setUnitMenuAnchor(e.currentTarget);
              }
            : undefined
        }
        selected={isSelected}
        selectedBlockPosition={selectedBlockPosition}
        onSelect={(e) => onSelectRow(row.id, e)}
        onContextMenu={contextMenu.onContextMenu}
        kebabLeadingSlot={
          <StatusCell
            status={isAiMapping ? 'aiMapped' : status}
            onToggleDiscard={isAiMapping ? undefined : onToggleDiscard}
          />
        }
        kebabLeadingInteractive
        trailingSlot={
          <>
            {isDiscardedStatus(status) && (
              <CandidateSaveMappingButton row={row} />
            )}
            <Box
              sx={{
                display: 'flex',
                flex: 1,
                minWidth: 0,
                alignSelf: 'stretch',
                opacity: isDiscardedStatus(status) ? 0.5 : 1,
              }}
            >
              <MetadataStrip
                headers={[...orderedHeaders]}
                values={rawWithActiveIfcQuantity(row, activeIfcMeasure)}
                ifcMeasurePicker={
                  fileMeasures.length > 0
                    ? {
                        row,
                        measures: fileMeasures,
                        active: activeIfcMeasure,
                        onPick: onPickIfcMeasure,
                        product: isRecipeMapping ? undefined : pickedProduct,
                        recipe: isRecipeMapping ? pickedRecipe : undefined,
                      }
                    : undefined
                }
              />
            </Box>
          </>
        }
      />
      <Menu
        open={unitMenuAnchor !== null}
        anchorEl={unitMenuAnchor}
        onClose={() => setUnitMenuAnchor(null)}
      >
        {unitMenuAnchor !== null &&
          quantityOptions.map((name) => (
            <Menu.Item
              key={name}
              selected={name === highlightedQuantity}
              icon={
                isQuantityIconName(name) ? (
                  <Icon category="quantity" icon={name} />
                ) : null
              }
              onClick={() => onPickQuantity(name)}
            >
              {labelFromValue(name, true)}
            </Menu.Item>
          ))}
      </Menu>
      {contextMenu.menu}
    </Box>
  );
};

/**
 * One imported CSV candidate row. Only mounts when {@link HideOutsideViewport}
 * shows its children (or when selected via `alwaysVisible`). Imported rows
 * route through {@link ImportedRowListItem} (Elements-tab editing surface);
 * incomplete rows route through {@link IncompleteRowListItem} (in-row
 * mapping editor).
 */
const CandidateRowListItem = React.memo<CandidateRowListItemProps>(
  function CandidateRowListItem({
    row,
    orderedHeaders,
    wasteScale,
    mappingsByCode,
    discardTriggerByRowId,
    stashedElements,
    productsById,
    recipesById,
    recipes,
    importedElements,
    importsById,
    sectionStatus,
    isMultiSelected,
    selectedBlockPosition,
    bulk,
    aiMapping,
    onSelectRow,
    onGoToElement,
    onDiscardRow,
    onSwitchMapping,
    onUnsetMapping,
  }) {
    const importedElement = importedElements.get(row.id);
    // A discarded row's element is removed from the tree but stashed; show its
    // data (read-only) instead of an empty "Select product" editor.
    const displayElement = importedElement ?? stashedElements.get(row.id);

    // Only materialized rows resolve their mapping by resource code (a
    // `stored`-source origin re-reads it). Rows without an element never look
    // up the DB mapping — no pre-fill, no auto "+" affordance.
    const mappingsForRow = importedElement ? mappingsByCode : EMPTY_MAPPINGS;

    // Subscribe to the per-row draft so an incomplete row's status reacts to
    // in-row / side-panel edits (e.g. becomes importable once product + rate
    // are set). Every materialize path clears the row's draft in its own change
    // batch (one undo frame; DEV-3457), so the overlay is empty for imported rows
    // — the old clear-on-mount effect (a localStorage leftover that pushed a
    // separate undo frame and wiped the redo stack) is gone.
    const draft = useImportRowDraft(row.id);
    const draftMappingsByRowId = useMemo(() => {
      const unitFrom = candidateUnitFrom(
        rowWithActiveIfcMeasure(row, draft.ifcMeasure),
      );
      const mapping = unitFrom
        ? draftToOriginMapping(draft, unitFrom)
        : undefined;
      return mapping ? { [row.id]: mapping } : undefined;
    }, [draft, row]);

    // The section already resolved this row's status (without a draft overlay).
    // Only a live draft can diverge from it, so recompute solely in that case —
    // otherwise reuse the section value and skip the baseline materialize.
    const status = useMemo(
      () =>
        !draftMappingsByRowId && sectionStatus
          ? sectionStatus
          : resolveCandidateStatus(
              row,
              mappingsForRow,
              discardTriggerByRowId,
              productsById,
              importedElements,
              {
                importsById,
                draftMappingsByRowId,
                recipesById,
                ifcMeasureByRowId: draft.ifcMeasure
                  ? { [row.id]: draft.ifcMeasure }
                  : undefined,
              },
            ),
      [
        sectionStatus,
        row,
        mappingsForRow,
        discardTriggerByRowId,
        productsById,
        recipesById,
        importedElements,
        importsById,
        draftMappingsByRowId,
        draft.ifcMeasure,
      ],
    );

    // Selection chrome follows multi-select (which already includes the active
    // row). Do not OR in URL id — that kept the previous row highlighted while
    // navigation caught up after a plain click.
    const isSelected = isMultiSelected;

    const handleToggleDiscard = useCallback(
      () => onDiscardRow(row.id),
      [onDiscardRow, row.id],
    );

    if (displayElement) {
      return (
        <ImportedRowListItem
          row={row}
          element={displayElement}
          elementInProject={Boolean(importedElement)}
          orderedHeaders={orderedHeaders}
          mappingsByCode={mappingsByCode}
          recipes={recipes}
          status={status}
          isSelected={isSelected}
          selectedBlockPosition={selectedBlockPosition}
          bulk={bulk}
          aiMapping={aiMapping}
          onSelectRow={onSelectRow}
          onGoToElement={onGoToElement}
          onToggleDiscard={handleToggleDiscard}
          onSwitchMapping={onSwitchMapping}
          onUnsetMapping={onUnsetMapping}
        />
      );
    }

    return (
      <IncompleteRowListItem
        row={row}
        orderedHeaders={orderedHeaders}
        wasteScale={wasteScale}
        productsById={productsById}
        recipesById={recipesById}
        recipes={recipes}
        status={status}
        isSelected={isSelected}
        selectedBlockPosition={selectedBlockPosition}
        bulk={bulk}
        aiMapping={aiMapping}
        onSelectRow={onSelectRow}
        onToggleDiscard={handleToggleDiscard}
      />
    );
  },
);

/** True when selection is a candidate row (or its imported element) in this file. */
const importSectionContainsCandidateSelection = (
  impId: string,
  selectedRowId: string,
  rowsByImport: Record<string, CandidateRow[]>,
  importedElements: ImportedElementsByRowId,
): boolean =>
  (rowsByImport[impId] ?? []).some(
    (row) =>
      row.id === selectedRowId ||
      importedElements.get(row.id)?.id === selectedRowId,
  );

/**
 * Imported candidate rows, grouped by import. Standalone port of the DEV-3144
 * `ImportedTabContent` POC, rewired to the element-imports store. Column
 * widths are user-resizable (per-import, persisted to `localStorage`).
 */
export const CandidateRowsTable: React.FC<{
  projectId: number;
  selectedRowId: string | null;
  /** Multi-selection by candidate row id (DEV-3456). */
  multiSelectedIds: ReadonlySet<string>;
  onSelectRow: (id: string, e: React.MouseEvent) => void;
}> = ({ projectId, selectedRowId, multiSelectedIds, onSelectRow }) => {
  const importsState = useElementImports(projectId);
  const version = useSelectedVersion();
  const scrollSync = useScrollSync();
  const searchFilter = useCandidateSearchFilter(projectId);
  const { is: isGroupExpanded } = useExpandedItems({
    defaultPrefix: 'csv-import',
  });
  const normalizedAccordionRef = useRef(false);
  const linkedElementId = useSelectedElementId();
  const importedElements = useImportedElements(version);

  const sortedImports = useMemo(() => {
    if (importsState.status !== 'ready') return [];
    return getImportsNewestFirst(
      importsState.data.importsById,
      version?.imports?.importIds,
    );
  }, [importsState, version?.imports?.importIds]);

  const visibleImports = sortedImports;

  const setImportExpanded = useCallback(
    (importId: string, next: boolean | ((prev: boolean) => boolean)) => {
      if (importsState.status !== 'ready') return;
      const importIds = Object.keys(importsState.data.importsById);
      const prev = isGroupExpanded(importId);
      const resolved = typeof next === 'boolean' ? next : next(prev);
      setCsvImportFileExpanded(importIds, importId, resolved);
    },
    [importsState, isGroupExpanded],
  );

  // Legacy localStorage may have several files expanded; keep one on first paint.
  useEffect(() => {
    if (importsState.status !== 'ready') return;
    if (normalizedAccordionRef.current) return;
    normalizedAccordionRef.current = true;

    const { importsById, rowsByImport } = importsState.data;
    const importIds = Object.keys(importsById);
    const expandedIds = importIds.filter((id) => isGroupExpanded(id));
    if (expandedIds.length <= 1) return;

    // Raw URL id, not `selectedRowId`: a collapsed file has no selectable rows.
    const withSelection =
      linkedElementId &&
      expandedIds.find((id) =>
        importSectionContainsCandidateSelection(
          id,
          linkedElementId,
          rowsByImport,
          importedElements,
        ),
      );
    const newestExpanded = expandedIds
      .map((id) => importsById[id])
      .filter((imp): imp is NonNullable<typeof imp> => imp !== undefined)
      .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))[0];
    const keepId = withSelection || newestExpanded?.id;
    if (!keepId) return;
    setCsvImportFileExpanded(importIds, keepId, true);
  }, [
    importsState,
    linkedElementId,
    version,
    isGroupExpanded,
    importedElements,
  ]);

  const importedElementsForClipboard = importedElements;
  const readyImports =
    importsState.status === 'ready' ? importsState.data : undefined;
  useImportMappingClipboardHotkeys({
    selectedRowId,
    multiSelectedIds,
    rowsByImport: readyImports?.rowsByImport ?? {},
    mappingsByCode: readyImports?.mappingsByCode ?? {},
    importsById: readyImports?.importsById ?? {},
    visibleImports,
    importedElements: importedElementsForClipboard,
  });

  if (importsState.status !== 'ready') return null;

  const { rowsByImport, mappingsByCode } = importsState.data;

  return (
    <ScrollSyncContext.Provider value={scrollSync}>
      <SharedIntersectionObserverProvider>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {visibleImports.map((imp, i) => (
            <ImportSection
              key={imp.id}
              imp={imp}
              rowsByImport={rowsByImport}
              mappingsByCode={mappingsByCode}
              selectedRowId={selectedRowId}
              multiSelectedIds={multiSelectedIds}
              onSelectRow={onSelectRow}
              searchFilter={searchFilter}
              isGroupExpanded={isGroupExpanded}
              setImportExpanded={setImportExpanded}
              allImportIds={Object.keys(importsState.data.importsById)}
              hideDivider={i === 0}
            />
          ))}
        </Box>
      </SharedIntersectionObserverProvider>
    </ScrollSyncContext.Provider>
  );
};

type ImportSectionProps = {
  imp: ProjectElementImport;
  rowsByImport: Record<string, CandidateRow[]>;
  mappingsByCode: MappingsByCode;
  selectedRowId: string | null;
  multiSelectedIds: ReadonlySet<string>;
  onSelectRow: (id: string, e: React.MouseEvent) => void;
  searchFilter: ReturnType<typeof useCandidateSearchFilter>;
  isGroupExpanded: (key: string) => boolean;
  setImportExpanded: (
    key: string,
    next: boolean | ((prev: boolean) => boolean),
  ) => void;
  allImportIds: readonly string[];
  hideDivider?: boolean;
};

const ImportSection: React.FC<ImportSectionProps> = ({
  imp,
  rowsByImport,
  mappingsByCode,
  selectedRowId,
  multiSelectedIds,
  onSelectRow,
  searchFilter,
  isGroupExpanded,
  setImportExpanded,
  allImportIds,
  hideDivider,
}) => {
  const { classes, cx } = useStyles();
  const version = useSelectedVersion();
  const importedElements = useImportedElements(version);
  const productsById = useProductsLookup();
  const recipesById = useRecipeLookup();
  const recipes = useRecipes();
  const navigateTo = useProjectNavigation();
  const applyChanges = useApplyProjectChanges();
  const handleGoToElement = useCallback(
    (elementId: string) => {
      // Center after the Elements tab paints; tab scroll-restore runs in a
      // layout effect, so the row scrolls on the following frame.
      requestScrollToElement(elementId, { align: 'center' });
      void navigateTo({ page: Page.Elements, elementId });
    },
    [navigateTo],
  );
  const handleDiscardRow = useCallback(
    (rowId: string) => {
      if (!version) return;
      const discard = !isRowDiscarded(version, rowId);
      const targets = resolveBulkActionTargetIds(
        rowId,
        multiSelectedIds,
        getVisibleListRowIds(),
      );
      if (targets.length === 0) return;
      void applyChanges(
        buildBulkDiscardCandidateRowChanges(version, targets, discard),
      );
    },
    [version, applyChanges, multiSelectedIds],
  );

  const importsByIdForSection = useMemo(() => ({ [imp.id]: imp }), [imp]);

  // Re-map a materialized row to a different product or recipe (DEV-3369/3387):
  // drop the old element and re-run the candidate flow with the new target seeded
  // on the per-row draft. A complete mapping re-materializes in the same batch; an
  // incomplete one leaves the row as a candidate to finish via the conversion UI.
  const bulk = useBulkRowActions({
    rows: rowsByImport[imp.id] ?? [],
    selectedIds: multiSelectedIds,
    mappingsByCode,
    importsById: importsByIdForSection,
  });
  const aiMapping = useAiImportMapping({
    rows: rowsByImport[imp.id] ?? [],
    selectedIds: multiSelectedIds,
  });

  const handleSwitchMapping = useCallback(
    (
      row: CandidateRow,
      element: IElement,
      id: string,
      type: MappingReferenceType,
    ) => {
      if (!version) return;
      if (bulk.shouldFanOut(row.id)) {
        bulk.setMapping(id, type);
        return;
      }

      // The picker only offers visible recipes, so a deleted target means a bug
      // upstream. Resolving an existing mapping to a deleted recipe is fine, but
      // re-mapping onto one must never happen.
      const targetRecipe = type === 'recipe' ? recipesById[id] : undefined;
      if (targetRecipe && isDeletedRecipe(targetRecipe)) {
        throw new Error(
          `Cannot map row to soft-deleted recipe "${targetRecipe.name}" (${id})`,
        );
      }

      // Preserve a user-typed quantity (number override) across the switch, even
      // when the new product's unit differs (DEV-3439). Without an override the
      // CSV Mängd maps onto the element quantity in materialization (auto for
      // kg/m³, user-disambiguated for m²/m, manual for non-physical units).
      const retainedQuantity = getImportQuantityOverride(element);
      const targetProduct = type === 'product' ? productsById[id] : undefined;
      const currentUnitFrom = candidateUnitFrom(
        rowWithActiveIfcMeasure(
          row,
          getImportRowDraft(version, row.id).ifcMeasure,
        ),
      );
      const currentOrigin =
        element.origin?.kind === 'csvImport' ? element.origin : undefined;
      const currentMapping =
        currentOrigin?.source === 'inline' ? currentOrigin.mapping : undefined;
      const currentEntry = currentUnitFrom
        ? currentMapping?.mappings[currentUnitFrom]
        : undefined;
      // Keep a still-valid calibration across the switch (wall → wall keeps its
      // m² rate); drop it only when the new target cannot address that quantity.
      const carriedEntry = isMappingCalibrationCompatible({
        entry: currentEntry,
        referenceType: type,
        product: targetProduct,
        recipe: type === 'recipe' ? recipesById[id] : undefined,
      })
        ? currentEntry
        : undefined;
      const { mapping, valueOverride } = buildSwitchMappingOverlay({
        row,
        referenceId: id,
        referenceType: type,
        product: targetProduct,
        retainedQuantity,
        carriedEntry,
      });

      const unitFrom = currentUnitFrom;
      const draftEntry = unitFrom ? mapping.mappings[unitFrom] : undefined;
      const existingIfcMeasure = getImportRowDraft(version, row.id).ifcMeasure;
      const nextDraft: ImportRowDraft = {
        referenceId: id,
        referenceType: type,
        ifcMeasure: existingIfcMeasure,
        ...(valueOverride !== undefined
          ? {
              totalQuantity: valueOverride,
              unitTo: draftEntry?.unitTo,
              quantityFactor: draftEntry?.quantityFactor,
            }
          : {}),
      };
      const result = tryMaterializeCandidateRow(row, {
        mappingsByCode,
        productsById,
        recipesById,
        importsById: importsByIdForSection,
        draftMappingsByRowId: { [row.id]: mapping },
        quantityOverridesByRowId:
          valueOverride !== undefined ? { [row.id]: valueOverride } : undefined,
        ifcMeasureByRowId: existingIfcMeasure
          ? { [row.id]: existingIfcMeasure }
          : undefined,
        trigger: 'user',
      });
      const changes: ProjectChange[] = [
        { type: ProjectChangeType.ElementRemove, elementId: element.id },
      ];
      if (result.ok) {
        // Merge the user's retained state onto the freshly mapped element and
        // re-add it under the same id (regenerateIds:false) so the switch keeps
        // the element identity + every non-imported edit.
        changes.push({
          type: ProjectChangeType.ElementAdd,
          elementId: importReaddParentId(version, element.id),
          factory: mergeReimportedElement(element, result.element),
          generateUniqueName: false,
          regenerateIds: false,
        });
      }
      // Fold the draft into the same batch as the element switch (one undo
      // frame): seed it only when the row stays incomplete; a materialized row
      // needs no draft, so clear any stale one.
      const draftChange = buildImportRowDraftReplaceChange(
        version,
        row.id,
        result.ok
          ? existingIfcMeasure
            ? { ifcMeasure: existingIfcMeasure }
            : null
          : nextDraft,
      );
      if (draftChange) changes.push(draftChange);
      void applyChanges(
        placeRemappedImportAdds(
          version,
          selectedProposalIdForVersion(version),
          changes,
        ),
      );
    },
    [
      version,
      bulk,
      mappingsByCode,
      productsById,
      recipesById,
      importsByIdForSection,
      applyChanges,
    ],
  );

  // Clear a materialized row's mapping (DEV-3506): remove the element and drop
  // product/factor/amount draft (keep IFC measure) so the row is a bare candidate.
  const handleUnsetMapping = useCallback(
    (row: CandidateRow, element: IElement) => {
      if (!version) return;
      const changes: ProjectChange[] = [
        { type: ProjectChangeType.ElementRemove, elementId: element.id },
      ];
      const draftChange = buildImportRowDraftReplaceChange(
        version,
        row.id,
        draftAfterRemovingMapping(getImportRowDraft(version, row.id)),
      );
      if (draftChange) changes.push(draftChange);
      void applyChanges(changes);
    },
    [version, applyChanges],
  );

  // Per-file row sort (localStorage). A stored column that no longer exists in
  // the import is ignored (→ default order). One column at a time.
  const [sort, setSort] = useState<ImportSort | undefined>(() =>
    loadImportSort(imp.id),
  );
  const effectiveSort = resolveImportSort(sort, imp.headers);
  const handleSortHeader = useCallback(
    (column: string) => {
      setSort((prev) => {
        const next = cycleImportSort(prev, column);
        saveImportSort(imp.id, next);
        return next;
      });
    },
    [imp.id],
  );

  const [hiddenStatusGroups, setHiddenStatusGroups] = useState<
    ReadonlySet<StatusSummaryGroupKey>
  >(() => loadImportStatusFilter(imp.id));

  // Default = original CSV order; an active header sort overrides it.
  const rows = useMemo(() => {
    const sourceRows = rowsByImport[imp.id] ?? [];
    const filtered =
      searchFilter.isActive && imp.id === searchFilter.scopeImportId
        ? sourceRows.filter(searchFilter.matches)
        : sourceRows;
    return effectiveSort
      ? sortCandidateRowsByColumn(filtered, effectiveSort)
      : sortCandidateRows(filtered);
  }, [rowsByImport, imp.id, searchFilter, effectiveSort]);
  // Memoized on version: getDiscardTriggerByRowId builds a fresh record each
  // call, and it feeds rowsWithStatus → targetVisibleRowIds, which drive the
  // status-filter animation effect. An unstable ref there re-fires the effect
  // every render → infinite setState loop.
  const discardTriggerByRowId = useMemo(
    () => getDiscardTriggerByRowId(version),
    [version],
  );
  const stashedElements = useMemo(
    () => getStashedElementsByRowId(version),
    [version],
  );
  const rowsWithStatus = useMemo(
    () =>
      rows.map((row) => ({
        row,
        status: resolveCandidateStatus(
          row,
          mappingsByCode,
          discardTriggerByRowId,
          productsById,
          importedElements,
          { importsById: importsByIdForSection, recipesById },
        ),
      })),
    [
      rows,
      mappingsByCode,
      recipesById,
      discardTriggerByRowId,
      productsById,
      importedElements,
      importsByIdForSection,
    ],
  );

  const counts: StatusCounts = {};
  for (const { status } of rowsWithStatus) {
    counts[status] = (counts[status] ?? 0) + 1;
  }

  // Row id → section-resolved status, so each mounted row reuses it instead of
  // recomputing (and re-materializing) its own status.
  const statusByRowId = useMemo(() => {
    const map = new Map<string, CandidateRowStatus>();
    for (const { row, status } of rowsWithStatus) map.set(row.id, status);
    return map;
  }, [rowsWithStatus]);

  const activeStatusGroups = useMemo(() => {
    const keys = new Set<StatusSummaryGroupKey>();
    for (const { status } of rowsWithStatus) {
      keys.add(getStatusSummaryGroupKey(status));
    }
    return keys;
  }, [rowsWithStatus]);

  const handleToggleStatusGroup = useCallback(
    (key: StatusSummaryGroupKey) => {
      setHiddenStatusGroups((prev) => {
        const next = toggleStatusSummaryGroupHidden(
          key,
          prev,
          activeStatusGroups,
        );
        saveImportStatusFilter(imp.id, next);
        return next;
      });
    },
    [activeStatusGroups, imp.id],
  );

  const targetVisibleRowIds = useMemo(() => {
    if (hiddenStatusGroups.size === 0) {
      return new Set(rowsWithStatus.map(({ row }) => row.id));
    }
    return new Set(
      rowsWithStatus
        .filter(
          ({ status }) => !isStatusGroupHidden(status, hiddenStatusGroups),
        )
        .map(({ row }) => row.id),
    );
  }, [rowsWithStatus, hiddenStatusGroups]);

  const visibleSelectedIds = useMemo(
    () => intersectSelectedWithVisible(multiSelectedIds, targetVisibleRowIds),
    [multiSelectedIds, targetVisibleRowIds],
  );

  useEffect(() => {
    setVisibleListRowIds(imp.id, targetVisibleRowIds);
  }, [imp.id, targetVisibleRowIds]);

  // Unregister on unmount only. Clearing on every `targetVisibleRowIds` change
  // would drop the stored set before re-adding it, which defeats the store's
  // value-equality check and emits two snapshot changes per render.
  useEffect(() => () => clearVisibleListRowIds(imp.id), [imp.id]);

  const { displayRows, onRowExitComplete } = useAnimatedStatusFilterRows(
    rowsWithStatus,
    targetVisibleRowIds,
  );

  // Selection borders collapse across *rendered* neighbours, so a row hidden by
  // the status filter splits the block visually while staying selected.
  const renderedRowIds = useMemo(
    () => displayRows.filter(({ visible }) => visible).map(({ row }) => row.id),
    [displayRows],
  );
  const firstRenderedRowId = renderedRowIds[0];
  const firstRenderedRowSelected =
    firstRenderedRowId !== undefined &&
    visibleSelectedIds.has(firstRenderedRowId);

  // Auto-size from the import's rows in their stable CSV order — never the
  // sorted/filtered view, or the sampled first-N rows (and thus the column
  // widths) would shift every time the user sorts or searches.
  const sampleRows = useMemo(
    () => (rowsByImport[imp.id] ?? []).map((r) => r.raw),
    [rowsByImport, imp.id],
  );
  const columnMap = imp.columnMap;
  const headerLabels = useMemo(
    () => buildHeaderLabels(imp.metadata?.catalog ?? []),
    [imp.metadata],
  );
  const { showUnusedColumns } = useShowUnusedColumns();
  const orderedHeaders = useMemo(() => {
    const sorted = sortColumnsByNodonPriority(imp.headers, columnMap);
    if (showUnusedColumns) return sorted;
    const mapped = new Set(
      Object.values(columnMap).map((h) => (h ? headerKey(h) : '')),
    );
    return sorted.filter((h) => mapped.has(headerKey(h)));
  }, [imp.headers, showUnusedColumns, columnMap]);
  const { columnWidths, onResizePointerDown } = useResizableColumnWidths(
    imp.id,
    orderedHeaders,
    sampleRows,
    headerLabels,
  );

  const importProgress = computeImportProgressFromStatusCounts(
    counts,
    rows.length,
  );
  const forceOpen =
    searchFilter.isActive && imp.id === searchFilter.scopeImportId;
  const expanded = forceOpen ? true : isGroupExpanded(imp.id);

  const stripeValue = useMemo<StripeCtx>(
    () => ({
      columnWidths,
      onResizePointerDown,
      headerLabels,
      sort: effectiveSort,
      onSortHeader: handleSortHeader,
      quantityHeader: columnMap.quantity,
      unitHeader: columnMap.unit,
      wasteHeader: columnMap.waste,
    }),
    [
      columnWidths,
      onResizePointerDown,
      headerLabels,
      effectiveSort,
      handleSortHeader,
      columnMap,
    ],
  );

  const handleSelectCandidateRow = useCallback(
    (id: string, e: React.MouseEvent) => onSelectRow(id, e),
    [onSelectRow],
  );

  const allCandidateRowIds = useMemo(
    () => (rowsByImport[imp.id] ?? []).map((row) => row.id),
    [rowsByImport, imp.id],
  );

  const kebabMenuOpen = useIsMenuOpen(imp.id);
  const handleKebabClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const anchorPosition = menuMouseHandler(e);
      if (anchorPosition) openMenu({ anchorPosition, id: imp.id });
    },
    [imp.id],
  );

  const sectionRef = useRef<HTMLDivElement>(null);
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const firstRowSentinelRef = useRef<HTMLDivElement>(null);
  const firstRowFlushWithHeader = useFirstRowFlushWithFileHeader(
    stickyHeaderRef,
    firstRowSentinelRef,
    firstRenderedRowSelected && expanded,
  );
  // After drop/navigate: keep the file open and park the *section* under the
  // list tabs. The sticky header's rect is the stuck visual top, so parking
  // against it is a no-op. Depend only on `imp.id` — `setImportExpanded`
  // changes every parent render and would cancel the wait/scroll.
  const allImportIdsRef = useRef(allImportIds);
  allImportIdsRef.current = allImportIds;
  useEffect(() => {
    const request = consumeScrollRequest(imp.id);
    if (!request) return;

    if (request.expandAfterScroll) {
      setCsvImportFileExpanded(allImportIdsRef.current, imp.id, true);
    }

    let settled = false;
    let cancelScroll: (() => void) | undefined;
    const parkSection = (): void => {
      const section = sectionRef.current;
      if (!section) {
        settled = true;
        return;
      }
      cancelScroll = scrollElementBelowListHeader(section, {
        onSettled: () => {
          settled = true;
        },
      });
    };
    const cancelWait = untilNextLayout(
      createParkedListTargetStabilizer(() => sectionRef.current),
      parkSection,
    );
    return () => {
      cancelWait();
      cancelScroll?.();
      // Strict remount / abort mid-scroll: re-queue so the next effect can finish.
      if (!settled) {
        requestScrollToElement(imp.id, {
          align: request.align,
          expandAfterScroll: request.expandAfterScroll,
        });
      }
    };
  }, [imp.id]);

  /** Collapse accordion; if mid-file sticky, jump the section up under the chrome. */
  const parkAfterUserExpandRef = useRef(false);
  const handleExpandedChange = useCallback(
    (next: boolean) => {
      if (!next && sectionRef.current) {
        scrollCollapsedStickySectionIntoPlace(sectionRef.current);
      }
      // Accordion open keeps `main` scroll; park this file's header after rows
      // mount so we don't inherit the previous file's offset.
      parkAfterUserExpandRef.current = next;
      setImportExpanded(imp.id, next);
      if (next) void navigateTo({ elementId: imp.id });
    },
    [imp.id, setImportExpanded, navigateTo],
  );
  useEffect(() => {
    if (!expanded || !parkAfterUserExpandRef.current) return;
    parkAfterUserExpandRef.current = false;
    return parkListSectionWhenReady(() => sectionRef.current, {
      behavior: 'auto',
    });
  }, [expanded]);

  // Deep link: a collapsed file drops its rows from the nav set, so `selectedRowId`
  // is already null by the time we could react to it. Read the raw URL id instead.
  // Fires once per id, so a file the user collapsed stays collapsed.
  const linkedElementId = useSelectedElementId();
  const handledLinkRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!linkedElementId || linkedElementId === 'none') return;
    if (handledLinkRef.current === linkedElementId) return;
    if (
      !importSectionContainsCandidateSelection(
        imp.id,
        linkedElementId,
        rowsByImport,
        importedElements,
      )
    ) {
      return;
    }
    // Record only once ownership is known: `importedElements` is derived from the
    // version, which can populate after the imports store. Recording earlier would
    // burn the id while the owning file is still unidentifiable.
    handledLinkRef.current = linkedElementId;
    if (isGroupExpanded(imp.id)) return;
    parkAfterUserExpandRef.current = true;
    setImportExpanded(imp.id, true);
  }, [
    linkedElementId,
    imp.id,
    rowsByImport,
    importedElements,
    isGroupExpanded,
    setImportExpanded,
  ]);

  return (
    <ImportStripeContext.Provider value={stripeValue}>
      {/* Section wrapper is the sticky containing block (header + rows). */}
      <Box ref={sectionRef}>
        {!hideDivider ? (
          <Box className={classes.importGroupDivider}>
            <Box className={classes.importColumnRail} />
          </Box>
        ) : null}
        <Box
          ref={stickyHeaderRef}
          className={expanded ? classes.stickyImportHeader : undefined}
        >
          <ElementListRow
            variant="group"
            name={formatImportLabel(imp)}
            nameOverrideSlot={<ImportFileRowLabel imp={imp} />}
            nameReadonly
            hideQuantity
            // Group gap is rendered above; keep it out of the sticky header.
            hideDivider
            expandable={!forceOpen}
            expanded={expanded}
            onExpandedChange={handleExpandedChange}
            selected={selectedRowId === imp.id}
            onSelect={(e) => onSelectRow(imp.id, e)}
            // Same menu as the MoreVert control (Delete import, …).
            onContextMenu={handleKebabClick}
            kebabLeadingNamePaddingRight={IMPORT_HEADER_STATUS_NAME_PAD}
            groupLeadingSlot={
              <Box sx={{ opacity: 0.4, display: 'flex' }}>
                <Icon
                  category="mui"
                  icon="InsertDriveFileOutlined"
                  size="small"
                />
              </Box>
            }
            nameTrailingSlot={(isRowHovered) => (
              <Box
                className={classes.fileHeaderKebab}
                component="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleKebabClick(e);
                }}
                sx={{
                  opacity: isRowHovered || kebabMenuOpen ? 1 : 0,
                }}
              >
                <Icon category="mui" icon="MoreVert" size={12} />
              </Box>
            )}
            kebabLeadingSlot={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <StatusSummary
                  counts={counts}
                  hiddenGroups={hiddenStatusGroups}
                  onToggleGroup={handleToggleStatusGroup}
                />
              </Box>
            }
            kebabLeadingInteractive
            trailingSlot={
              expanded ? (
                <MetadataStrip headers={orderedHeaders} />
              ) : (
                <ImportProgressStrip
                  resolved={importProgress.resolved}
                  total={importProgress.total}
                />
              )
            }
          />
          {/* Thin rule under the import header (column labels) before rows. */}
          {expanded ? (
            <Box
              className={cx(
                classes.importHeaderDivider,
                firstRowFlushWithHeader && classes.importHeaderDividerSelected,
              )}
            />
          ) : null}
        </Box>
        {expanded ? (
          <Box
            ref={firstRowSentinelRef}
            className={classes.firstRowSentinel}
            aria-hidden
          />
        ) : null}
        <ImportKebabMenu
          importId={imp.id}
          candidateRowIds={allCandidateRowIds}
        />
        {expanded ? (
          <Box className={classes.importSectionRows}>
            {displayRows.map(({ row, visible }) => {
              const isMultiSelected = visibleSelectedIds.has(row.id);
              return (
                <AnimatedCandidateRowSlot
                  key={row.id}
                  visible={visible}
                  alwaysVisible={isMultiSelected}
                  onExitComplete={() => onRowExitComplete(row.id)}
                >
                  <CandidateRowListItem
                    row={row}
                    orderedHeaders={orderedHeaders}
                    wasteScale={imp.metadata?.interpretations?.wasteScale}
                    mappingsByCode={mappingsByCode}
                    discardTriggerByRowId={discardTriggerByRowId}
                    stashedElements={stashedElements}
                    productsById={productsById}
                    recipesById={recipesById}
                    recipes={recipes}
                    importedElements={importedElements}
                    importsById={importsByIdForSection}
                    sectionStatus={statusByRowId.get(row.id)}
                    isMultiSelected={isMultiSelected}
                    selectedBlockPosition={selectedBlockPositionBelowFileHeader(
                      getSelectedBlockPosition(
                        renderedRowIds,
                        visibleSelectedIds,
                        row.id,
                      ),
                      row.id === firstRenderedRowId,
                    )}
                    bulk={bulk}
                    aiMapping={aiMapping}
                    onSelectRow={handleSelectCandidateRow}
                    onGoToElement={handleGoToElement}
                    onDiscardRow={handleDiscardRow}
                    onSwitchMapping={handleSwitchMapping}
                    onUnsetMapping={handleUnsetMapping}
                  />
                </AnimatedCandidateRowSlot>
              );
            })}
            {forceOpen && rows.length === 0 ? (
              <Box
                sx={{
                  p: 3,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  No imported rows match “{searchFilter.search}”.
                </Typography>
              </Box>
            ) : null}
          </Box>
        ) : null}
      </Box>
    </ImportStripeContext.Provider>
  );
};
